<template>
  <div class="people-page">
    <div style="height: 215px">
      <PlotPayments
        :startDate="startDate"
        :endDate="endDate"
        :peopleId="selectedPayment ? selectedPayment.Pid : null"
        :refreshPlot="refreshPaymentPlot"
        :currentCycleStats="currentCycleStats"
        @show-uncollected-leases="openUncollectedLeasesPopup"
        @select-clearing-payments="selectClearingPayments"
      />
    </div>

    <div class="search-input">
      <!-- Property Search Bar for table -->
      <div class="search-bar">
        <input
          ref="searchQueryInput"
          v-model="inputQuery"
          type="text"
          placeholder="Search for pmt"
          title="Search tokens: :b (blank address), !20 (exclude $20), [mobile] (PixieChatBot payments)"
          class="search-input"
          style="width: 90px"
        />
        <button @click="clearSearch" class="clear-button">X</button>
        <!-- Start Date Picker -->
        <label>from: </label>
        <input
          v-model="startDate"
          type="date"
          class="date-picker"
          @change="onDateChange"
        />

        <!-- End Date Picker -->
        <label>to: </label>
        <input
          v-model="endDate"
          type="date"
          class="date-picker"
          @change="onDateChange"
        />
        <button
          :title="'previous cycle'"
          @click="setDateRange('previousCycle')"
        >
          PC
        </button>
        <button :title="'current cycle'" @click="setDateRange('currentCycle')">
          CC
        </button>
        <button :title="'this year to date'" @click="setDateRange('ytd')">
          TY
        </button>
        <button :title="'this week to date'" @click="setDateRange('week')">
          1W
        </button>
        <button :title="'this month to date'" @click="setDateRange('month')">
          1M
        </button>
        <!-- Row Count and Sum -->
        <label>Count: </label
        ><span class="count" style="display: inline-block; width: 30px">
          {{ formatNumber(rowCount, 0) }}</span
        >
        <label>Sum: </label
        ><span class="sum" style="display: inline-block; width: 74px">
          {{ formatNumber(totalSum, 2) }}</span
        >
        <button
          :title="'refresh payments'"
          @click="
            latestPayment = null;
            fetchPayments();
          "
        >
          <i class="fas fa-refresh"></i>
        </button>
        <button
          :title="'filter for empty addresses'"
          :class="{ 'filter-active': filterForEmptyAddress }"
          @click="toggleFilterForEmptyAddress"
        >
          <i class="fas fa-map-marker-alt"></i>
        </button>
        <button
          style="position: relative; margin-left: 10px"
          :title="'filter for checks'"
          @click="toggleFilterForChecks"
          :class="{ 'filter-active': filterForChecks }"
        >
          <i class="fas fa-money-check"></i>
        </button>
        <button
          style="position: relative"
          :title="'filter for e-checks'"
          @click="toggleFilterForEchecks"
          :class="{ 'filter-active': filterForEchecks }"
        >
          <i class="fas fa-credit-card"></i>
        </button>
        <button
          style="position: relative"
          :title="'filter for ungrouped'"
          @click="toggleFilterForUngrouped"
          :class="{ 'filter-active': filterForUngrouped }"
        >
          <i class="fas fa-object-ungroup"></i>
        </button>
        <button
          style="position: relative"
          :title="'filter for Grouped'"
          @click="toggleFilterForGrouped"
          :class="{ 'filter-active': filterForGrouped }"
        >
          <i class="fas fa-layer-group"></i>
        </button>
        <button
          style="position: relative; margin-left: 4px"
          :title="'add to deposit group'"
          @click="groupIntoDeposit"
        >
          <i class="fas fa-list-1-2"></i>
        </button>
        <button
          style="position: relative; margin-left: 4px"
          :title="'remove from deposit group'"
          @click="clearDeposit"
        >
          <i class="fas fa-eraser"></i>
        </button>

        <button
          style="position: relative; margin-left: 4px"
          :title="'edit deposit group'"
          @click="editDepositGroup($event)"
        >
          <i class="fas fa-pencil"></i>
        </button>

        <button
          style="position: relative; margin-left: 4px"
          :title="'reassign payment to different tenant'"
          @click="openReassignModal"
        >
          <i class="fas fa-right-left"></i>
        </button>
        <button
          style="position: relative; width: 30px"
          :title="'look for new payments'"
          @click="runDownload"
        >
          <i class="fas fa-download"></i>
          <span
            @click="runDownload($event)"
            title="receipts are ready to import"
            v-if="unreadReceipts > 0"
            class="notification-dot"
            >{{ unreadReceipts }}</span
          >
        </button>
      </div>
    </div>

    <div class="table-container">
      <div class="table-header">
        <table>
          <thead>
            <tr>
              <th
                v-for="(header, index) in paymentTableHeaders"
                :key="index"
                :style="{
                  minWidth: header.width,
                  maxWidth: header.width,
                  textAlign:
                    header.align === 'right'
                      ? 'right'
                      : header.align === 'left'
                        ? 'left'
                        : 'center',
                }"
              >
                {{ header.label }}
                <div @click="sortBy(header.field)">
                  <i
                    class="fas"
                    :class="{
                      'fa-sort': sortField !== header.field,
                      'fa-sort-up':
                        sortField === header.field && sortOrder === 'asc',
                      'fa-sort-down':
                        sortField === header.field && sortOrder === 'desc',
                    }"
                    title="Sort"
                  ></i>
                </div>
              </th>
            </tr>
          </thead>
        </table>
      </div>

      <!-- Scrollable body container -->
      <div class="table-body table-outer-wrapper">
        <div class="table-inner-wrapper">
          <table>
            <tbody>
              <tr
                v-for="(payment, index) in filteredPayments"
                :key="payment.id"
                @dblclick="handleDoubleClick($event, payment)"
                @click="selectRow($event, payment, index)"
                :class="{
                  'selected-row': selectedPayments
                    ? selectedPayments.some(
                        (selected) => selected.id === payment.id,
                      )
                    : '', // Highlight selected rows based on propertyId
                  'odd-row': index % 2 === 1,
                  'even-row': index % 2 === 0,
                }"
              >
                <td
                  class="address"
                  :title="'double click to view leases\nfor this address'"
                  :style="{ minWidth: '100px', maxWidth: '100px' }"
                >
                  <span
                    v-if="
                      (!payment.address || payment.address === 'no address') &&
                      parseFloat(payment.amount) !== 20
                    "
                    style="background: #9e85a6; color: white; padding: 2px"
                  >
                    select row, reassign pmt
                  </span>
                  <span v-else>{{ payment.address }}</span>
                </td>
                <td
                  class="fullName"
                  :title="'double click to search \nfor this tenant'"
                  :style="{
                    minWidth: '100px',
                    maxWidth: '100px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }"
                >
                  {{ payment.fullName }}
                </td>
                <td
                  :style="{
                    minWidth: '50px',
                    maxWidth: '50px',
                    textAlign: 'right',
                    backgroundColor: isFailedAmountStatus(payment)
                      ? '#FFEBEE'
                      : undefined,
                    color: isFailedAmountStatus(payment)
                      ? '#B71C1C'
                      : undefined,
                  }"
                >
                  {{ payment.amount }}
                </td>
                <td
                  class="lease-balance"
                  :title="leaseBalanceTitle(payment.leaseBalance)"
                  :style="{
                    minWidth: '70px',
                    maxWidth: '70px',
                    textAlign: 'right',
                    color: leaseBalanceColor(payment.leaseBalance),
                  }"
                >
                  {{ formatLeaseBalance(payment.leaseBalance) }}
                </td>
                <td
                  class="payment-lstat lease-state"
                  :title="leaseStateTitle(payment)"
                  :style="{
                    minWidth: '40px',
                    maxWidth: '40px',
                    textAlign: 'center',
                    color: payment.leaseStateForeground || '',
                    backgroundColor: payment.leaseStateBackground || '',
                    cursor: payment.LID ? 'pointer' : 'default',
                  }"
                  @dblclick.stop="openLeaseStateFromPayment(payment)"
                >
                  {{ payment.leaseState || '' }}
                </td>
                <td
                  :style="{
                    minWidth: '50px',
                    maxWidth: '50px',
                    textAlign: 'center',
                  }"
                >
                  {{ formatDate(payment.paidOn) }}
                </td>
                <td
                  class="transactionNumber"
                  :title="
                    'Status: ' +
                    payment.status +
                    '\nDouble click to email failed payment notification'
                  "
                  :style="{
                    minWidth: '70px',
                    maxWidth: '70px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: getStatusColor(payment).text
                      ? getStatusColor(payment).text
                      : undefined,
                    backgroundColor: getStatusColor(payment).background
                      ? getStatusColor(payment).background
                      : undefined,
                  }"
                >
                  {{ payment.transactionNumber }}
                </td>
                <td
                  class="Email"
                  :title="
                    (payment.Email || '') +
                    '\n\ndouble-click: email tenant\nshift+double-click: People Search'
                  "
                  :style="{
                    minWidth: '150px',
                    maxWidth: '150px',
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }"
                >
                  {{ payment.Email }}
                </td>
                <td
                  class="td-payment-type"
                  :style="{
                    minWidth: '56px',
                    maxWidth: '56px',
                    textAlign: 'left',
                    color: '#666',
                  }"
                  v-html="getPaymentIcon(payment).html"
                  :title="getPaymentIcon(payment).title"
                  @dblclick.stop="onPaymentTypeDblClick(payment, $event)"
                ></td>
                <td
                  class="td-purpose"
                  :style="{
                    minWidth: '40px',
                    maxWidth: '40px',
                    textAlign: 'center',
                  }"
                >
                  <div
                    v-if="editingPurpose === payment.id"
                    class="position-wrapper"
                  >
                    <select
                      ref="autoOpenSelect"
                      v-model="selectedPurpose"
                      @change="updatePurpose(payment)"
                      class="custom-select"
                    >
                      <option
                        v-for="(label, value) in paymentPurposeMap"
                        :key="label + '-' + value"
                        :value="value"
                      >
                        {{ label }}
                      </option>
                    </select>
                  </div>
                  <div v-else>
                    {{ payment.purpose }}
                  </div>
                </td>
                <td
                  class="td-status"
                  :style="{
                    minWidth: '40px',
                    maxWidth: '40px',
                    textAlign: 'center',
                  }"
                >
                  <div
                    v-if="editingStatus === payment.id"
                    class="position-wrapper"
                  >
                    <select
                      ref="autoOpenStatusSelect"
                      v-model="selectedStatus"
                      @change="updateStatus(payment)"
                      class="custom-select"
                    >
                      <option
                        v-for="(label, value) in paymentStatusMap"
                        :key="'status-' + label + '-' + value"
                        :value="value"
                      >
                        {{ label }}
                      </option>
                    </select>
                  </div>
                  <div v-else>
                    {{ payment.status }}
                  </div>
                </td>
                <td
                  class="td-depositGroup"
                  :style="{
                    minWidth: '56px',
                    maxWidth: '56px',
                    textAlign: 'center',
                    color: '#666',
                    whiteSpace: 'nowrap',
                  }"
                  :title="
                    payment.depositGroup
                      ? 'Deposit Group: ' +
                        payment.depositGroup +
                        '\n' +
                        formatDate(payment.depositGroupDate) +
                        '\nClick: filter by this deposit\nDouble-click: find in bank ledger'
                      : 'No deposit group'
                  "
                >
                  {{ payment.depositGroup?.slice(-8) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="date-container">
      transactions updated:
      <span style="display: inline-block; width: 150px; text-align: left">
        {{ formatRelativeTime(forteTransactionsUpdated) }}
      </span>
      payouts updated:
      <span style="display: inline-block; width: 150px; text-align: left">
        {{ formatRelativeTime(fortePayoutsUpdated) }}
      </span>

      <!-- Add a button to open the Payment Statuses modal -->
      <button @click="openPaymentStatusesModal" class="modal-open-button">
        Explain Payment Status
      </button>
    </div>

    <!-- Modal Popup for Payment Statuses -->
    <div v-if="showPaymentStatusesModal" class="modal-overlay">
      <div class="modal-content">
        <span class="modal-title">How to interpret status codes</span>
        <button class="modal-close-button" @click="closePaymentStatusesModal">
          Close
        </button>
        <div class="modal-body" v-html="paymentStatusesHtml"></div>
      </div>
    </div>

    <!-- Modal Popup for Editing Deposit Group -->
    <div v-if="showEditDepositGroupModal" class="modal-overlay-positioned">
      <div
        class="modal-content edit-deposit-modal positioned-modal"
        :style="getModalStyle()"
        ref="editDepositModal"
      >
        <span class="modal-title">Edit Deposit Group</span>
        <div class="modal-body edit-deposit-body">
          <input
            v-model="editingDepositGroupValue"
            type="text"
            class="edit-deposit-input"
            placeholder="Enter deposit group"
            ref="depositGroupInput"
          />
          <div class="modal-buttons">
            <button @click="saveDepositGroup" class="save-button">Save</button>
            <button @click="cancelDepositGroupEdit" class="cancel-button">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Reassign Payment Modal -->
    <div
      v-if="showReassignModal"
      class="modal-overlay"
      @click="closeReassignModal"
    >
      <div class="reassign-modal" @click.stop>
        <div class="modal-header">
          <span class="modal-title">Reassign Payment to Tenant</span>
          <button
            class="modal-close-button"
            @click="closeReassignModal"
            title="Close"
          >
            ×
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="tenantSearch">Search Tenant Name</label>
            <div class="autocomplete-wrapper">
              <input
                ref="tenantSearchInput"
                id="tenantSearch"
                v-model="reassignSearchQuery"
                type="text"
                placeholder="Type tenant name..."
                @input="handleReassignSearch"
                @focus="showReassignSuggestions = true"
                @blur="handleReassignBlur"
                @keydown="handleReassignKeydown"
                autocomplete="off"
              />
              <div
                v-if="showReassignSuggestions && reassignSuggestions.length > 0"
                class="autocomplete-dropdown"
              >
                <div
                  v-for="(suggestion, index) in reassignSuggestions"
                  :key="suggestion.id"
                  :class="[
                    'autocomplete-item',
                    { highlighted: selectedReassignIndex === index },
                  ]"
                  @mousedown.prevent="selectReassignTenant(suggestion)"
                  @mouseover="selectedReassignIndex = index"
                >
                  <div class="suggestion-name">{{ suggestion.fullName }}</div>
                  <div class="suggestion-details">
                    <span v-if="suggestion.Phone" class="suggestion-phone">{{
                      suggestion.Phone
                    }}</span>
                    <span
                      v-if="suggestion.Address"
                      class="suggestion-address"
                      >{{ suggestion.Address }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="selectedReassignTenant" class="selected-tenant-info">
            <strong>Selected Tenant:</strong>
            {{ selectedReassignTenant.fullName }}
            <br />
            <span v-if="selectedReassignTenant.Address">
              Address: {{ selectedReassignTenant.Address }}
            </span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-button cancel" @click="closeReassignModal">
            Close
          </button>
          <button
            class="modal-button save"
            @click="updatePaymentTenant"
            :disabled="!selectedReassignTenant"
          >
            Update
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Uncollected Leases Popup (floating panel — no full-screen underlay so
       Quick Email and other UI stay usable) -->
  <Teleport to="body">
    <div
      v-if="showUncollectedLeasesPopup"
      ref="uncollectedPopupContent"
      class="uncollected-popup-content"
    >
      <div class="uncollected-popup-header uncollected-popup-drag-handle">
        <i class="fas fa-grip-horizontal" aria-hidden="true"></i>
        <span v-if="uncollectedLeasesPopupMode === 'balance'">
          Unpaid Balances — Current Cycle ({{
            filteredUncollectedLeasesList.length
          }})
        </span>
        <span v-else>
          Uncollected Rent —
          {{
            uncollectedLeasesCycle === 'previous'
              ? 'Previous Cycle'
              : 'Current Cycle'
          }}
          ({{ filteredUncollectedLeasesList.length }})
        </span>
      </div>
      <div class="uncollected-popup-body">
        <div
          v-if="uncollectedLeasesLoading"
          class="uncollected-popup-loading"
        >
          Loading...
        </div>
        <template v-else>
          <div
            class="uncollected-popup-search"
            :class="{
              'uncollected-popup-search--balance':
                uncollectedLeasesPopupMode === 'balance',
            }"
          >
            <input
              v-model="uncollectedLeasesSearch"
              placeholder="Search by address or tenant..."
              class="uncollected-popup-search-input"
            />
            <button
              v-if="uncollectedLeasesSearch"
              class="uncollected-popup-clear-search"
              @click="uncollectedLeasesSearch = ''"
            >
              <i class="fas fa-times"></i>
            </button>
            <button
              v-if="uncollectedLeasesPopupMode === 'balance'"
              type="button"
              class="uncollected-balance-sort-toggle"
              :title="uncollectedBalancesSortTitle"
              @click="cycleUncollectedBalancesSort"
            >
              {{ uncollectedBalancesSortLabel }}
            </button>
          </div>
          <div class="uncollected-leases-list">
            <div
              v-for="(lease, index) in filteredUncollectedLeasesList"
              :key="index"
              class="uncollected-lease-item"
            >
              <div class="uncollected-lease-info">
                <div
                  class="uncollected-lease-address address"
                  :title="'double click to view leases\nfor this address'"
                  @dblclick="navigateToLeaseSearch(lease.property)"
                >
                  <i
                    v-if="lease.bouncedPayment"
                    class="fas fa-exclamation-triangle payment-warning-icon"
                    :title="`Payment of ${lease.bounceAmount} bounced on ${lease.bounceDate}`"
                  ></i>
                  {{ lease.property }}
                </div>
                <div
                  class="uncollected-lease-tenant"
                  :title="
                    lease.emails
                      ? 'double-click to email tenant'
                      : 'No email on file for this tenant'
                  "
                  :style="{
                    cursor: lease.emails ? 'pointer' : 'default',
                  }"
                  @dblclick.stop="openEmailFromUncollected(lease)"
                >
                  {{ lease.tenant }}
                </div>
              </div>
              <div class="uncollected-lease-details">
                <span
                  class="uncollected-lease-lstat lease-state"
                  :title="
                    (lease.leaseStateDescription ||
                      'Most recent lease state') +
                    (lease.leaseStateDate ? '\n' + lease.leaseStateDate : '') +
                    (lease.leaseId
                      ? '\n\ndouble-click to edit lease status'
                      : '')
                  "
                  :style="{
                    color: lease.leaseStateForeground || '',
                    backgroundColor: lease.leaseStateBackground || '',
                    cursor: lease.leaseId ? 'pointer' : 'default',
                  }"
                  @dblclick.stop="openLeaseStateFromUncollected(lease)"
                >
                  {{ lease.leaseState || '—' }}
                </span>
                <div
                  class="uncollected-lease-amount"
                  :title="
                    uncollectedLeasesPopupMode === 'balance'
                      ? leaseBalanceTitle(lease.balanceValue)
                      : 'Monthly rent'
                  "
                >
                  {{
                    uncollectedLeasesPopupMode === 'balance'
                      ? lease.balanceOwed
                      : lease.rentOwed
                  }}
                </div>
                <div
                  v-if="uncollectedLeasesPopupMode !== 'balance'"
                  class="uncollected-lease-balance"
                  :title="leaseBalanceTitle(lease.balanceValue)"
                  :style="{ color: leaseBalanceColor(lease.balanceValue) }"
                >
                  {{ lease.balance }}
                </div>
              </div>
            </div>
            <div
              v-if="filteredUncollectedLeasesList.length > 0"
              class="uncollected-grand-total"
            >
              <span class="uncollected-grand-total-label">Grand Total</span>
              <span class="uncollected-grand-total-amount">
                {{ uncollectedLeasesPopupListTotal }}
              </span>
            </div>
            <div
              v-if="filteredUncollectedLeasesList.length === 0"
              class="uncollected-no-results"
            >
              No unpaid leases found for this cycle.
            </div>
          </div>
        </template>
      </div>
      <div class="uncollected-popup-footer">
        <div class="uncollected-popup-footer-actions">
          <button
            v-if="unpaidPopupShowsNotifyButton"
            type="button"
            :disabled="
              uncollectedLeasesLoading ||
              filteredUncollectedLeasesList.length === 0
            "
            title="Email and tenant-app message each unpaid tenant"
            @click="openUncollectedNotifyPopup"
          >
            Notify
          </button>
          <button
            v-if="uncollectedLeasesPopupMode !== 'balance'"
            type="button"
            :disabled="uncollectedLeasesLoading"
            :title="
              uncollectedLeasesCycle === 'previous'
                ? 'Show current cycle (25th–24th)'
                : 'Show previous cycle (25th–24th)'
            "
            @click="toggleUncollectedLeasesCycle"
          >
            {{
              uncollectedLeasesCycle === 'previous'
                ? 'Current cycle'
                : 'Prev cycle'
            }}
          </button>
          <button
            type="button"
            :disabled="
              uncollectedLeasesLoading ||
              filteredUncollectedLeasesList.length === 0
            "
            @click="printUncollectedLeasesList"
          >
            Print
          </button>
          <button
            type="button"
            :disabled="uncollectedLeasesLoading"
            @click="refreshUncollectedLeasesPopup"
          >
            {{ uncollectedLeasesLoading ? 'Refreshing…' : 'Refresh' }}
          </button>
          <button type="button" @click="closeUncollectedLeasesPopup">
            Close
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div
      v-if="showUncollectedNotifyPopup"
      class="uncollected-notify-overlay"
      @mousedown.self="onUncollectedNotifyOverlayClick"
    >
      <div class="uncollected-notify-popup" @mousedown.stop>
        <div class="uncollected-notify-header">
          Notify unpaid tenants
          <span v-if="uncollectedNotifyQueue.length" class="uncollected-notify-count">
            {{ uncollectedNotifyIndex + 1 }} / {{ uncollectedNotifyQueue.length }}
          </span>
        </div>
        <div class="uncollected-notify-body">
          <div v-if="uncollectedNotifyDone" class="uncollected-notify-done">
            {{ uncollectedNotifyDoneMessage }}
          </div>
          <template v-else-if="uncollectedNotifyCurrent">
            <div class="uncollected-notify-to-row">
              <label class="uncollected-notify-to-label">To:</label>
              <div class="uncollected-notify-to">
                {{ uncollectedNotifyTo }}
                <span
                  class="uncollected-notify-balance"
                  :title="leaseBalanceTitle(uncollectedNotifyCurrent.balanceValue)"
                  :style="{
                    color: leaseBalanceColor(
                      uncollectedNotifyCurrent.balanceValue,
                    ),
                  }"
                >
                  {{ uncollectedNotifyCurrent.balance }}
                </span>
              </div>
            </div>
            <label class="uncollected-notify-to-label">Message</label>
            <div class="uncollected-notify-preview-wrap">
              <div
                class="uncollected-notify-preview"
                v-html="uncollectedNotifyPreviewHtml"
              ></div>
              <div
                v-if="uncollectedNotifySending || uncollectedNotifySendAll"
                class="uncollected-notify-sending"
              >
                <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                {{
                  uncollectedNotifySendAll ? 'Sending all…' : 'Sending…'
                }}
              </div>
            </div>
          </template>
          <div
            v-if="uncollectedNotifyErrors.length"
            class="uncollected-notify-errors"
          >
            <div
              v-for="(err, i) in uncollectedNotifyErrors"
              :key="i"
            >
              {{ err }}
            </div>
          </div>
        </div>
        <div class="uncollected-notify-footer">
          <button
            type="button"
            :disabled="uncollectedNotifyBusy || uncollectedNotifyDone"
            @click="skipUncollectedNotify"
          >
            Skip
          </button>
          <button
            type="button"
            :disabled="uncollectedNotifyBusy || uncollectedNotifyDone"
            @click="sendUncollectedNotify"
          >
            <i
              v-if="uncollectedNotifySending && !uncollectedNotifySendAll"
              class="fas fa-spinner fa-spin"
              aria-hidden="true"
            ></i>
            {{
              uncollectedNotifySending && !uncollectedNotifySendAll
                ? 'Sending…'
                : 'Send'
            }}
          </button>
          <button
            type="button"
            :disabled="uncollectedNotifyBusy || uncollectedNotifyDone"
            @click="sendUncollectedNotifyAll"
          >
            <i
              v-if="uncollectedNotifySendAll"
              class="fas fa-spinner fa-spin"
              aria-hidden="true"
            ></i>
            {{ uncollectedNotifySendAll ? 'Sending all…' : 'All' }}
          </button>
          <button type="button" @click="closeUncollectedNotifyPopup">
            Close
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Lease status editor (same as Manage Properties LStat) -->
  <LeaseState
    :leaseId="selectedLeaseStateLeaseId"
    v-model:showStatesDialog="showLeaseStateDialog"
    @update:showStatesDialog="onLeaseStateDialogVisibility"
  />

  <!-- Check image viewer (paper / cashier / money-order) — same pattern as LeaseSearch -->
  <Teleport to="body">
    <div
      v-if="showCheckViewer"
      ref="checkViewer"
      class="check-viewer draggable-popup"
    >
      <div class="check-viewer-header">
        <div
          class="check-viewer-title"
          @mousedown.stop.prevent="startCheckViewerDrag"
        >
          Check Viewer
          <i class="fas fa-crosshairs draggable-icon" title="Drag window"></i>
        </div>
        <button
          type="button"
          class="check-viewer-close-button"
          @click="closeCheckViewer"
        >
          X
        </button>
      </div>
      <div v-if="checkViewerLoading" class="check-viewer-loading">
        Loading check image…
      </div>
      <iframe
        v-else-if="checkViewerUrl"
        class="check-viewer-frame"
        :src="checkViewerUrl"
        frameborder="0"
        title="Check image"
      ></iframe>
      <div v-else class="check-viewer-loading">No check image available.</div>
    </div>
  </Teleport>

  <!-- Footer Message -->
  <div :class="{ 'footer-message': true, highlighted: footerMessage !== '' }">
    {{ footerMessage }}
  </div>

  <!-- Teleport so z-index stacks above NavBar floaters (notes, work orders, quick email) -->
  <Teleport to="body">
    <div
      v-if="showToolsPage"
      ref="toolsPageWrapper"
      class="tools-page-wrapper"
    >
      <div class="tools-page-drag-handle">
        <i class="fas fa-grip-horizontal" aria-hidden="true"></i>
        <span
          >This window will close automatically if there are no errors.</span
        >
      </div>
      <ToolsPage
        :toolData="selectedRunTool"
        :canClose="true"
        @close="handleToolsPageClose"
      />
    </div>
  </Teleport>
</template>

<script>
import axios from '@/axiosConfig'; // Adjust based on your axios configuration
import PlotPayments from './PlotPayments.vue';
import ToolsPage from './ToolsPage.vue'; // Adjust the path if necessary
import LeaseState from './LeaseState.vue';
import eventBus from './eventBus.js';
import io from 'socket.io-client';
import { marked } from 'marked'; // Make sure marked is installed via npm/yarn
import { makeDraggable } from '@/utils/makeDraggable';
import {
  buildUnpaidLeasesList,
  calculateUncollectedRent,
  calculateUnpaidBalancesTotal,
  filterClearingPayments,
  getClearingCycleContext,
  getCurrentCyclePeriod,
  getPreviousCyclePeriod,
  isCountableCyclePayment,
  isPaymentInCycle,
  sumClearingPayments,
} from '@/utils/unpaidLeases.js';
import {
  buildUncollectedNotifyMarkdown,
  notifyToLabel,
} from '@/utils/uncollectedRentNotify.js';
import {
  beginUncollectedNotifySend,
  endUncollectedNotifySend,
  filterUncollectedNotifyQueue,
  markUncollectedNotifySentToday,
  wasUncollectedNotifySentToday,
} from '@/utils/uncollectedRentNotifySent.js';
import { unpaidPopupShowsNotify } from '@/utils/unpaidPopupNotify.js';

function formatDateForInput(date) {
  const adjustedDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000,
  );
  return adjustedDate.toISOString().split('T')[0];
}

function formatCycleCurrency(amount) {
  if (!amount) return '$0';
  return (
    '$' +
    Math.round(Number(amount))
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  );
}

/** Currency with cents (clearing sum must match selected-row total). */
function formatCycleCurrencyExact(amount) {
  const n = Number(amount);
  if (!n) return '$0.00';
  const sign = n < 0 ? '-' : '';
  return (
    sign +
    '$' +
    Math.abs(n)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  );
}

function escapePrintHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export default {
  components: {
    PlotPayments,
    ToolsPage,
    LeaseState,
  },
  created() {
    // Fetch properties and wait for completion
    this.setDates();

    this.fetchPayments()
      .then(() => {
        // Check for a saved property in local storage after properties are fetched
        const savedPayments = localStorage.getItem('selectedPayments');

        if (savedPayments) {
          try {
            // Parse the stored payments and set selectedPayments
            this.selectedPayments = JSON.parse(savedPayments);

            // Ensure the selectedPayments array is valid and all payments match an item in paymentData
            const isValid = this.selectedPayments.every((savedPayment) =>
              this.paymentData.some(
                (payment) => payment.id === savedPayment.id,
              ),
            );

            if (!isValid) {
              // If selectedPayments are not valid or do not match, clear them
              console.log('Selected payments are not valid.');
              this.selectedPayments = [];
              localStorage.removeItem('selectedPayments');
            }
          } catch (error) {
            console.error(
              'Error parsing selectedPayments from local storage:',
              error,
            );
            // Clear the corrupted or undefined local storage entry
            this.selectedPayments = [];
            localStorage.removeItem('selectedPayments');
          }
        }
        if (this.$route.query.search) {
          this.inputQuery = this.$route.query.search;
          this.searchQuery = this.$route.query.search;

          // Check if search param looks like MMDDYY.N format (date with sequence)
          const dateMatch = this.$route.query.search.match(
            /^(\d{2})(\d{2})(\d{2})\.?\d*$/,
          );
          if (dateMatch) {
            const [, mm, dd, yy] = dateMatch;
            const year =
              parseInt(yy) < 50 ? 2000 + parseInt(yy) : 1900 + parseInt(yy);
            const month = parseInt(mm) - 1; // JS months are 0-indexed
            const day = parseInt(dd);
            const parsedDate = new Date(year, month, day);

            // Use parsed date as start date if valid
            if (!isNaN(parsedDate.getTime())) {
              this.startDate = parsedDate.toISOString().split('T')[0];
            }
          }
          // If not a date format, don't reset startDate - keep what setDates() set

          this.latestPayment = null; // Reset to force full refresh with new date range
          this.fetchPayments();
        }
      })
      .catch((error) => {
        // Handle errors that occur during fetchPayments
        console.error('Failed to fetch payments:', error);
      });

    // Fetch balance data
    this.getBalance();

    this.fetchUnreadCounts(); // Initial fetch
    this.timer = setInterval(this.fetchUnreadCounts, 120000); // Fetch every 60 seconds
    this.fetchCurrentCycleStats();
  },

  data() {
    return {
      hasStartDate: false,
      unreadReceipts: 0,
      startDate: null,
      endDate: null,
      clickTimeout: null,
      inputQuery: '',
      searchQuery: '',
      paymentData: [],
      leaseBalanceByLeaseId: {},
      lastSelectedIndex: null,
      selectedPayments: [],
      currentCycleStats: null,

      paymentTableHeaders: [
        { label: 'Address', field: 'Address', width: '100px', align: 'left' },
        { label: 'Tenant', field: 'fullName', width: '100px', align: 'left' },
        { label: 'Amount', field: 'amount', width: '50px', align: 'right' },
        {
          label: 'Balance',
          field: 'leaseBalance',
          width: '70px',
          align: 'right',
        },
        {
          label: 'Lstat',
          field: 'leaseState',
          width: '40px',
          align: 'center',
        },
        { label: 'Paid On', field: 'paidOn', width: '50px', align: 'center' },
        {
          label: 'Transact #',
          field: 'transactionNumber',
          width: '70px',
          align: 'left',
        },
        { label: 'Email', field: 'Email', width: '150px', align: 'left' },
        { label: 'Type', field: 'method', width: '56px', align: 'left' },
        { label: 'Purp', field: 'purpose', width: '40px', align: 'center' },
        { label: 'Status', field: 'status', width: '40px', align: 'center' },
        {
          label: 'DepId',
          field: 'depositGroup',
          width: '56px',
          align: 'center',
        },
      ],
      selectedPayment: null,
      sortField: 'Paid On',
      sortOrder: 'desc',
      footerMessage: 'messages will appear here.',
      showToolsPage: false,
      toolsPageDragCleanup: null,
      latestPayment: null,
      updateCheckInterval: null,
      footerTimeout: null,
      debounceTimeout: null, // To store the debounce timer
      refreshPaymentPlot: false,
      editingPurpose: null,
      selectedPurpose: null,
      paymentPurposeMap: {
        0: '',
        1: 'Rent',
        2: 'Prorated',
        3: 'Fees',
        4: 'Sec Dep',
        5: 'Appl.',
      },
      editingStatus: null,
      selectedStatus: null,
      paymentStatusMap: {
        0: '',
        1: 'Early',
        2: 'Late',
        3: 'Rejected',
        4: 'Returned',
        5: 'Retrying',
        6: 'Settling',
        7: 'Voided',
        8: 'PADV', // payment advice
        9: 'Partial',
      },
      socket: null,
      filterForChecks: false,
      filterForEchecks: false,
      filterForUngrouped: false,
      filterForGrouped: false,
      filterForEmptyAddress: false,
      filterOutEpayments: false,
      forteTransactionsUpdated: null,
      fortePayoutsUpdated: null,
      showPaymentStatusesModal: false,
      paymentStatusesHtml: '',
      showEditDepositGroupModal: false,
      editingDepositGroupValue: '',
      modalPosition: { x: 0, y: 0, isTopHalf: true },
      showReassignModal: false,
      reassignSearchQuery: '',
      reassignSuggestions: [],
      showReassignSuggestions: false,
      selectedReassignIndex: -1,
      selectedReassignTenant: null,
      reassignSearchTimeout: null,
      showUncollectedLeasesPopup: false,
      uncollectedLeasesList: [],
      uncollectedLeasesLoading: false,
      uncollectedLeasesSearch: '',
      uncollectedLeasesPopupMode: 'rent',
      uncollectedLeasesCycle: 'current',
      uncollectedBalancesSort: 'default',
      uncollectedPopupDragCleanup: null,
      showLeaseStateDialog: false,
      selectedLeaseStateLeaseId: null,
      showUncollectedNotifyPopup: false,
      uncollectedNotifyQueue: [],
      uncollectedNotifyIndex: 0,
      uncollectedNotifySending: false,
      uncollectedNotifySendAll: false,
      uncollectedNotifyErrors: [],
      uncollectedNotifyDone: false,
      showCheckViewer: false,
      checkViewerUrl: null,
      checkViewerLoading: false,
      checkViewerDocId: null,
      isCheckViewerDragging: false,
      checkViewerDragOffset: { x: 0, y: 0 },
    };
  },

  mounted() {
    this.connectSocket();
    document.addEventListener('mousemove', this.handleCheckViewerDrag);
    document.addEventListener('mouseup', this.stopCheckViewerDrag);

    // Listen for search by address event from ManageProperties
    eventBus.on('searchPaymentsByAddress', (address) => {
      this.inputQuery = address;
    });

    // Listen for payment download trigger from NavBar badge
    eventBus.on('triggerPaymentDownload', () => {
      this.runDownload();
    });

    // Fetch recent operations and update forte*updated fields accordingly
    axios
      .get('/api/recent-operation')
      .then((response) => {
        const operations = response.data;
        operations.forEach((row) => {
          if (row.operation === 'get Forte transactions') {
            this.forteTransactionsUpdated = row.completedOn;
          } else if (row.operation === 'get Forte payouts') {
            this.fortePayoutsUpdated = row.completedOn;
          }
        });
      })
      .catch((error) => {
        console.error('Error fetching recent operations:', error);
      });

    this.setDates(); // Set the startDate and endDate when the component mounts
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('resize', this.handleUncollectedPopupResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        this.handleUncollectedPopupResize,
      );
      window.visualViewport.addEventListener(
        'scroll',
        this.handleUncollectedPopupResize,
      );
    }
    this.footerTimeout = setTimeout(() => {
      this.footerMessage = '';
    }, 5000);

    this.updateCheckInterval = setInterval(() => {
      this.fetchPayments();
    }, 30000); // 30,000ms = 30 seconds
    if (this.$refs.searchQueryInput) {
      this.$refs.searchQueryInput.focus();
      if (this.hasStartDate) {
        this.inputQuery = ':b,!20';
      }
    }
  },

  beforeUnmount() {
    eventBus.off('searchPaymentsByAddress');
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.handleUncollectedPopupResize);
    document.removeEventListener('mousemove', this.handleCheckViewerDrag);
    document.removeEventListener('mouseup', this.stopCheckViewerDrag);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener(
        'resize',
        this.handleUncollectedPopupResize,
      );
      window.visualViewport.removeEventListener(
        'scroll',
        this.handleUncollectedPopupResize,
      );
    }
    clearTimeout(this.updateCheckInterval);
    clearTimeout(this.footerTimeout);
    this.revokeCheckViewerUrl();
    if (this.uncollectedPopupDragCleanup) {
      this.uncollectedPopupDragCleanup();
      this.uncollectedPopupDragCleanup = null;
    }
    if (this.toolsPageDragCleanup) {
      this.toolsPageDragCleanup();
      this.toolsPageDragCleanup = null;
    }
  },

  computed: {
    filteredUncollectedLeasesList() {
      const query = this.uncollectedLeasesSearch.trim().toLowerCase();
      let list = this.uncollectedLeasesList;

      if (query) {
        list = list.filter((lease) => {
          const property = (lease.property || '').toLowerCase();
          const tenant = (lease.tenant || '').toLowerCase();
          const leaseState = (lease.leaseState || '').toLowerCase();
          return (
            property.includes(query) ||
            tenant.includes(query) ||
            leaseState.includes(query)
          );
        });
      }

      if (this.uncollectedLeasesPopupMode !== 'balance') {
        return list;
      }

      const sorted = [...list];
      if (this.uncollectedBalancesSort === 'desc') {
        sorted.sort(
          (a, b) => (b.balanceOwedValue || 0) - (a.balanceOwedValue || 0),
        );
      } else if (this.uncollectedBalancesSort === 'asc') {
        sorted.sort(
          (a, b) => (a.balanceOwedValue || 0) - (b.balanceOwedValue || 0),
        );
      } else {
        sorted.sort((a, b) => a.property.localeCompare(b.property));
      }

      return sorted;
    },

    uncollectedBalancesSortLabel() {
      if (this.uncollectedBalancesSort === 'desc') {
        return 'Balance ↓';
      }
      if (this.uncollectedBalancesSort === 'asc') {
        return 'Balance ↑';
      }
      return 'Address';
    },

    uncollectedBalancesSortTitle() {
      if (this.uncollectedBalancesSort === 'desc') {
        return 'Sorted by balance, highest first. Click for lowest first.';
      }
      if (this.uncollectedBalancesSort === 'asc') {
        return 'Sorted by balance, lowest first. Click for address order.';
      }
      return 'Sorted by address. Click for highest balance first.';
    },

    unpaidPopupShowsNotifyButton() {
      return unpaidPopupShowsNotify(this.uncollectedLeasesPopupMode);
    },
    uncollectedNotifyBusy() {
      return this.uncollectedNotifySending || this.uncollectedNotifySendAll;
    },
    uncollectedNotifyCurrent() {
      if (this.uncollectedNotifyDone) return null;
      return this.uncollectedNotifyQueue[this.uncollectedNotifyIndex] || null;
    },
    uncollectedNotifyTo() {
      const lease = this.uncollectedNotifyCurrent;
      if (!lease) return '';
      return notifyToLabel({
        tenant: lease.tenant,
        property: lease.property,
      });
    },
    uncollectedNotifyPreview() {
      const lease = this.uncollectedNotifyCurrent;
      if (!lease) return '';
      const person = lease.peopleId || '…';
      const balanceOnly = this.uncollectedLeasesPopupMode === 'balance';
      return buildUncollectedNotifyMarkdown({
        tenant: lease.tenant,
        amount: lease.rentOwed,
        balance: balanceOnly ? lease.balanceOwed : lease.balance,
        balanceOnly,
        ledgerUrl: `https://tenantapp.bigtapapp.com/?d=ledger&person=${person}&key=(one-time)`,
      });
    },
    uncollectedNotifyPreviewHtml() {
      const md = this.uncollectedNotifyPreview;
      return md ? marked.parse(md) : '';
    },
    uncollectedNotifyDoneMessage() {
      if (!this.uncollectedNotifyQueue.length) {
        return 'All unpaid tenants in this list were already notified today.';
      }
      return 'All notifications in this list have been processed.';
    },
    uncollectedLeasesPopupListTotal() {
      const total = this.filteredUncollectedLeasesList.reduce((sum, lease) => {
        if (this.uncollectedLeasesPopupMode === 'balance') {
          return sum + (lease.balanceOwedValue || 0);
        }
        return sum + (lease.rentOwedValue || 0);
      }, 0);
      return formatCycleCurrency(total);
    },

    // Compute the row count, prioritizing selectedPayments if any
    rowCount() {
      // Check if selectedPayments has any items
      if (this.selectedPayments) {
        const payments =
          this.selectedPayments.length > 0
            ? this.selectedPayments
            : this.filteredPayments;
        return Array.isArray(payments) ? payments.length : 0;
      } else return 0;
    },

    // Compute the sum of the amount field, prioritizing selectedPayments if any
    totalSum() {
      // Check if selectedPayments has any items
      if (this.selectedPayments) {
        // Prioritize selectedPayments over filteredPayments
        const payments =
          this.selectedPayments.length > 0
            ? this.selectedPayments
            : this.filteredPayments;

        // Ensure payments is an array
        if (!Array.isArray(payments)) return '0.00';

        // Reduce the array to calculate the total sum, ensuring doc is valid and has an amount property
        return payments
          .reduce((sum, doc) => {
            // Ensure doc is defined and has a valid amount
            if (doc && doc.amount != null) {
              const amount = parseFloat(doc.amount) || 0;
              return sum + amount;
            }
            return sum;
          }, 0)
          .toFixed(2); // Ensure sum is fixed to two decimal places
      } else {
        return '0.00';
      }
    },

    filteredPayments() {
      if (!Array.isArray(this.paymentData)) return [];

      const startsWithBlankCheck = this.inputQuery?.startsWith(':b');

      const tokens = this.inputQuery
        ?.split(',')
        .map((token) => token.trim().toLowerCase());

      const normalizedTokens = (tokens || []).map((token) =>
        token.replace(/^['"]|['"]$/g, ''),
      );
      const bracketFilters = normalizedTokens.filter((token) =>
        token.match(/^\[.*\]$/),
      );
      const regularTokens = normalizedTokens.filter(
        (token) => !token.match(/^\[.*\]$/),
      );

      let filteredResults;

      if (startsWithBlankCheck) {
        filteredResults = this.paymentData.filter(
          (payment) => !payment.address || payment.address.trim() === '',
        );
      } else {
        filteredResults = this.paymentData;
      }

      /* apply additional filters before checking for search tokens */
      if (this.filterForChecks) {
        filteredResults = filteredResults.filter((payment) =>
          [1, 3, 4, 5].includes(payment.method),
        );
      }
      if (this.filterForEchecks) {
        filteredResults = filteredResults.filter(
          (payment) => payment.method === 2,
        );
      }
      if (this.filterForUngrouped) {
        filteredResults = filteredResults.filter(
          (payment) =>
            (payment.depositGroup === '' || payment.depositGroup === null) &&
            payment.purpose !== 'Fees',
        );
      }

      if (this.filterForGrouped) {
        filteredResults = filteredResults.filter(
          (payment) =>
            payment.depositGroup !== '' &&
            payment.depositGroup !== null &&
            ![3, 4, 5].includes(payment.purpose),
        );
      }

      if (this.filterForEmptyAddress) {
        filteredResults = filteredResults.filter(
          (payment) => !payment.address || payment.address.trim() === '',
        );
      }

      if (bracketFilters.length > 0) {
        bracketFilters.forEach((bracket) => {
          const filterType = bracket.slice(1, -1).toLowerCase();
          if (filterType === 'mobile') {
            filteredResults = filteredResults.filter(
              (payment) => payment.paidBy === 'chatBot',
            );
          }
        });
      }

      if (
        !regularTokens.length ||
        regularTokens[0] === '' ||
        (regularTokens.length === 1 && regularTokens[0] === ':b')
      ) {
        return filteredResults;
      }

      // Check for the presence of '!20' in the tokens
      const excludeAmount20 = regularTokens.includes('!20');

      regularTokens.forEach((token) => {
        if (token !== ':b' && token !== '!20') {
          filteredResults = filteredResults.filter((payment) => {
            const paymentValues = Object.values(payment).map((value) =>
              value ? value.toString().toLowerCase() : '',
            );

            return paymentValues.some((value) => value.includes(token));
          });
        }
      });

      // Apply additional filter to exclude payments with amount 20 if '!20' token is present
      if (excludeAmount20) {
        filteredResults = filteredResults.filter(
          (payment) => payment.amount !== '20.00',
        );
      }
      return filteredResults;
    },

    getPaymentIcon() {
      return (payment) => {
        const method = payment?.method;
        // Always reserve a third icon slot so letter+check rows line up with
        // letter+check+mobile (ChatBot) rows.
        // Inline styles — v-html content is outside Vue scoped CSS.
        const letterStyle =
          'display:inline-block;width:0.9em;text-align:center;' +
          'font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,' +
          '"Liberation Mono","Courier New",monospace;font-weight:600';
        const letter = (ch) =>
          `<span style="${letterStyle}">${ch}</span>`;
        const mobileSlot =
          payment?.paidBy === 'chatBot'
            ? ' <i class="fas fa-mobile-alt" style="display:inline-block;width:1em;text-align:center" title="via ChatBot"></i>'
            : ' <span style="display:inline-block;width:1em" aria-hidden="true"></span>';
        const chatBotTitleSuffix =
          payment?.paidBy === 'chatBot' ? ' via ChatBot' : '';
        const isSecDep =
          payment?.purpose === 'Sec Dep' || Number(payment?.purpose) === 4;
        const offerStatus = isSecDep ? null : payment?.earlyPaymentOffer;
        const offerStar =
          offerStatus === 'eligible'
            ? ' <i class="far fa-star" style="display:inline-block;width:1em;text-align:center;color:#c9a227" title="Early Payment offer: tenant-app e-check after email"></i>'
            : offerStatus === 'pending'
              ? ' <i class="fas fa-star" style="display:inline-block;width:1em;text-align:center;color:#c9a227" title="Early Payment offer: emailed, no tenant-app e-check yet"></i>'
              : ' <span style="display:inline-block;width:1em" aria-hidden="true"></span>';
        const offerTitleSuffix =
          offerStatus === 'eligible'
            ? '; Early Payment offer (tenant-app e-check after email)'
            : offerStatus === 'pending'
              ? '; Early Payment offer (emailed, no tenant-app e-check yet)'
              : '';
        // Paper / cashier / money-order: double-click opens check image (when docId present)
        const checkIcon = (faClass, titleExtra = '') => {
          const hasDoc = payment?.docId;
          const titlePart = hasDoc
            ? ' — double-click to view check image'
            : ' — no check image linked';
          return (
            ` <i class="fas ${faClass} payment-check-image-icon"` +
            ` style="cursor:${hasDoc ? 'pointer' : 'default'}"` +
            ` title="${titleExtra}${titlePart}"></i>`
          );
        };

        switch (method) {
          case 1:
            return {
              html: `${letter('P')}${checkIcon('fa-money-check', 'paper check')}${mobileSlot}${offerStar}`,
              title: `paper check${chatBotTitleSuffix}${offerTitleSuffix}`,
            };
          case 2:
            return {
              html: `${letter('E')} <i class="fas fa-money-check-alt"></i>${mobileSlot}${offerStar}`,
              title: `E-check (DEX)${chatBotTitleSuffix}${offerTitleSuffix}`,
            };
          case 3:
            return {
              html: `${letter('C')}${checkIcon('fa-money-check', 'cashiers check')}${mobileSlot}${offerStar}`,
              title: `cashiers check${chatBotTitleSuffix}${offerTitleSuffix}`,
            };
          case 4:
            return {
              html: `${letter('O')} <i class="fas fa-money-bill"></i>${mobileSlot}${offerStar}`,
              title: `other${chatBotTitleSuffix}${offerTitleSuffix}`,
            };
          case 5:
            return {
              html: `${letter('M')}${checkIcon('fa-money-check-alt', 'money order')}${mobileSlot}${offerStar}`,
              title: `money order${chatBotTitleSuffix}${offerTitleSuffix}`,
            };
          case 6:
            return {
              html: `${letter('B')} <i class="fas fa-file-invoice-dollar"></i>${mobileSlot}${offerStar}`,
              title: `billpayment svc${chatBotTitleSuffix}${offerTitleSuffix}`,
            };
          default:
            return {
              html: `${letter('?')} <i class="fas fa-question"></i>${mobileSlot}${offerStar}`,
              title: `unknown${offerTitleSuffix}`,
            };
        }
      };
    },
  },

  methods: {
    // Function to connect to WebSocket server
    connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;

        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          return;
        }

        const token = this.getAuthToken();

        this.socket = io(socketUrl, {
          query: {
            token: token,
            source: 'paymentViewer',
          },
        });

        this.socket.on('connect', () => {
          console.log('paymentViewer: Connected to WebSocket server');
        });

        /*
        this.socket.onAny((event, ...args) => {
          console.log(`Received event: ${event}`, args);
        });
        */

        (this.socket.on('taskCompleted', (data) => {
          // if data.script == "update_forte_transactions"
          // then set this.forteTransactionsUpdated = data.timestamp
          if (data.script === 'processUnreadReceipts.js') {
            if (data.code === 0) {
              this.handleToolsPageClose();
            } else {
              this.fetchPayments(true);
            }
          }
          if (data.script === 'update_forte_transactions.js') {
            this.forteTransactionsUpdated = data.timestamp;
            this.fetchPayments();
          }
          if (data.script === 'update_forte_payouts.js') {
            this.fortePayoutsUpdated = data.timestamp;
            this.fetchPayments();
          }
        }),
          this.socket.on('leasePaymentUpdated', (data) => {
            if (!data || data.id == null) return;
            const existingPaymentIndex = this.paymentData.findIndex(
              (payment) => payment.id === data.id,
            );
            const existing =
              existingPaymentIndex >= 0
                ? this.paymentData[existingPaymentIndex]
                : null;
            const paymentRow = {
              ...data,
              purpose: this.paymentPurposeMap[data.purpose] || '',
              status: this.paymentStatusMap[data.status] || '',
              leaseBalance:
                this.leaseBalanceFor(data.LID) ??
                existing?.leaseBalance ??
                null,
              leaseState: data.leaseState ?? existing?.leaseState ?? '',
              leaseStateDescription:
                data.leaseStateDescription ??
                existing?.leaseStateDescription ??
                '',
              leaseStateForeground:
                data.leaseStateForeground ??
                existing?.leaseStateForeground ??
                '',
              leaseStateBackground:
                data.leaseStateBackground ??
                existing?.leaseStateBackground ??
                '',
              leaseStateDate:
                data.leaseStateDate ?? existing?.leaseStateDate ?? '',
              earlyPaymentOffer:
                data.earlyPaymentOffer ?? existing?.earlyPaymentOffer ?? null,
            };

            if (existingPaymentIndex >= 0) {
              this.paymentData.splice(existingPaymentIndex, 1, paymentRow);
            } else {
              this.paymentData.unshift(paymentRow);
            }
          }));

        this.socket.on('unreadReceiptsChanged', this.fetchUnreadCounts);

        this.socket.on('disconnect', () => {
          console.log('Disconnected from WebSocket server');
        });
      } catch (error) {
        console.error('Error connecting to WebSocket server:', error);
      }
    },

    clearSearch() {
      this.inputQuery = '';
      this.searchQuery = '';
      this.selectedPayment = null;
      this.filterForEmptyAddress = false;
    },

    /** Paper check (1), cashiers check (3), money order (5) can open check PDF. */
    isCheckImageMethod(method) {
      return method === 1 || method === 3 || method === 5;
    },

    onPaymentTypeDblClick(payment, event) {
      if (!this.isCheckImageMethod(payment?.method)) return;

      // Prefer double-click on the check icon (not letter / mobile spacer)
      const t = event?.target;
      if (t && t.tagName === 'I' && !t.classList.contains('payment-check-image-icon')) {
        return;
      }

      if (!payment.docId) {
        this.footerMessage =
          'No check image is linked to this payment (missing docId).';
        return;
      }

      this.openCheckViewer(payment.docId);
    },

    revokeCheckViewerUrl() {
      if (this.checkViewerUrl && this.checkViewerUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(this.checkViewerUrl);
        } catch (_) {
          /* ignore */
        }
      }
      this.checkViewerUrl = null;
    },

    closeCheckViewer() {
      this.showCheckViewer = false;
      this.checkViewerLoading = false;
      this.checkViewerDocId = null;
      this.revokeCheckViewerUrl();
    },

    async openCheckViewer(docId) {
      this.checkViewerDocId = docId;
      this.showCheckViewer = true;
      this.checkViewerLoading = true;
      this.revokeCheckViewerUrl();

      try {
        const response = await axios.get(`/api/load-pdf/${docId}`, {
          responseType: 'arraybuffer',
        });
        if (response.status === 200) {
          const pdfBlob = new Blob([response.data], {
            type: 'application/pdf',
          });
          this.checkViewerUrl = URL.createObjectURL(pdfBlob);
          this.footerMessage = `Check image loaded (document ${docId}).`;
        } else {
          this.footerMessage = 'Failed to load check image: server error.';
        }
      } catch (error) {
        console.error('Error loading check PDF:', error);
        this.footerMessage = 'Failed to load check image.';
        this.checkViewerUrl = null;
      } finally {
        this.checkViewerLoading = false;
      }
    },

    startCheckViewerDrag(event) {
      const popupEl = this.$refs.checkViewer;
      if (!popupEl) return;

      const popupRect = popupEl.getBoundingClientRect();
      popupEl.style.left = `${popupRect.left}px`;
      popupEl.style.top = `${popupRect.top}px`;
      popupEl.style.right = 'auto';
      popupEl.style.transform = 'none';

      this.isCheckViewerDragging = true;
      this.checkViewerDragOffset = {
        x: event.clientX - popupRect.left,
        y: event.clientY - popupRect.top,
      };
      document.body.style.userSelect = 'none';
    },

    handleCheckViewerDrag(event) {
      if (!this.isCheckViewerDragging) return;
      const popupEl = this.$refs.checkViewer;
      if (!popupEl) return;
      popupEl.style.left = `${event.clientX - this.checkViewerDragOffset.x}px`;
      popupEl.style.top = `${event.clientY - this.checkViewerDragOffset.y}px`;
    },

    stopCheckViewerDrag() {
      if (!this.isCheckViewerDragging) return;
      this.isCheckViewerDragging = false;
      document.body.style.userSelect = '';
    },

    async handleToolsPageClose() {
      // Handle the closing of the ToolsPage component
      this.showToolsPage = false;
      this.refreshPaymentPlot = true;
      this.unreadReceipts = 0;
      this.sortKey = 'createdOn';
      this.sortOrder = -1;
      this.latestPayment = null;
      await this.fetchPayments(true);
      await this.fetchUnreadCounts();
    },

    runDownload(event) {
      if (event) {
        event.stopPropagation();
      }
      this.openToolsPage(2, '');
      this.setDateRange('week');

      return;
    },

    openToolsPage(toolId, options) {
      // Open ToolsPage with the given toolId and options
      this.selectedRunTool = {
        toolId: toolId,
        options: options,
      };
      this.showToolsPage = true;
    },

    initToolsPageDrag() {
      if (this.toolsPageDragCleanup) {
        this.toolsPageDragCleanup();
        this.toolsPageDragCleanup = null;
      }
      const el = this.$refs.toolsPageWrapper;
      if (!el) return;
      this.toolsPageDragCleanup = makeDraggable(el, {
        handleSelector: '.tools-page-drag-handle',
        storageKeyPrefix: 'paymentViewerToolsPage',
      });
    },

    async setDateRange(range) {
      // Get the current date
      // Get current date in America/New_York timezone
      const options = { timeZone: 'America/New_York' };
      const nyTime = new Date().toLocaleString('en-US', options);
      const nyDate = new Date(nyTime);

      let today = new Date(nyTime);

      let startDate = new Date(today);
      if (range === 'ytd') {
        startDate.setFullYear(today.getFullYear(), 0, 1);
      }
      if (range === 'today') startDate.setDate(today.getDate());
      if (range === 'week') startDate.setDate(today.getDate() - 7);
      if (range === 'month') startDate.setDate(today.getDate() - 30);
      if (range === 'currentCycle') {
        // If we're on or after the 25th of the current month, use 25th of current month to today
        if (nyDate.getDate() >= 25) {
          startDate = new Date(nyDate.getFullYear(), nyDate.getMonth(), 25);
        } else {
          // Otherwise use 25th of previous month to today
          startDate = new Date(nyDate.getFullYear(), nyDate.getMonth() - 1, 25);
        }
      }
      if (range === 'previousCycle') {
        // If we're on or after the 25th of the current month:
        if (nyDate.getDate() >= 25) {
          // Start date is 25th of previous month
          startDate = new Date(nyDate.getFullYear(), nyDate.getMonth() - 1, 25);
          // End date is 24th of current month
          today = new Date(nyDate.getFullYear(), nyDate.getMonth(), 24);
        } else {
          // Otherwise if we're before the 25th:
          // Start date is 25th of two months ago
          startDate = new Date(nyDate.getFullYear(), nyDate.getMonth() - 2, 25);
          // End date is 24th of previous month
          today = new Date(nyDate.getFullYear(), nyDate.getMonth() - 1, 24);
        }
      }

      // Check if there is a 'start' parameter in the URL and update startDate if present
      if (this.$route.query.startdate) {
        const startParam = this.$route.query.startdate;
        // Validate the date format here if necessary
        startDate = new Date(startParam);
        this.hasStartDate = true;
      }

      const endDate = today;
      // Update the component's data properties with formatted values
      this.startDate = formatDateForInput(startDate);
      this.endDate = formatDateForInput(endDate);
      await this.fetchPayments(true);
      if (range === 'currentCycle' || range === 'previousCycle') {
        this.$nextTick(() => {
          // Filter out Sec Deposit, Rejected, and Returned payments before assigning to selectedPayments
          this.selectedPayments = this.filteredPayments.filter(
            isCountableCyclePayment,
          );
        });
      }
    },

    setDates() {
      // Get the current date
      const today = new Date();

      // Default start date is 180 days ago
      let startDate = new Date(today);
      startDate.setDate(today.getDate() - 180);

      // Check if there is a 'start' parameter in the URL and update startDate if present
      if (this.$route.query.startdate) {
        const startParam = this.$route.query.startdate;
        // Validate the date format here if necessary
        startDate = new Date(startParam);
        this.hasStartDate = true;
      }

      // Check if search param looks like MMDDYY.N format (date with sequence)
      const searchParam = this.$route.query.search;
      if (searchParam) {
        this.inputQuery = searchParam;
        this.searchQuery = searchParam;
        const dateMatch = searchParam.match(/^(\d{2})(\d{2})(\d{2})\.?\d*$/);
        if (dateMatch) {
          const [, mm, dd, yy] = dateMatch;
          const year =
            parseInt(yy) < 50 ? 2000 + parseInt(yy) : 1900 + parseInt(yy);
          const month = parseInt(mm) - 1; // JS months are 0-indexed
          const day = parseInt(dd);
          const parsedDate = new Date(year, month, day);

          // Use parsed date as start date if valid
          if (!isNaN(parsedDate.getTime())) {
            startDate = parsedDate;
          }
        }
      }

      // Determine the endDate based on the presence of startParam
      const endDate = this.$route.query.startdate
        ? new Date(startDate.getFullYear(), startDate.getMonth() + 2, 0)
        : new Date(today);

      // Update the component's data properties with formatted values
      this.startDate = formatDateForInput(startDate);
      this.endDate = formatDateForInput(endDate);
    },

    getAuthToken() {
      return localStorage.getItem('token');
    },

    setAuthHeaders() {
      axios.defaults.headers.common['Authorization'] =
        `Bearer ${this.getAuthToken()}`;
    },

    debounce(func, delay) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout); // Clear the previous timeout
        timeout = setTimeout(() => {
          func.apply(this, args); // Apply the function after the delay
        }, delay);
      }.bind(this);
    },

    async fetchPayments(force = false) {
      this.setAuthHeaders();

      if (force) {
        this.latestPayment = null;
      }

      return axios
        .get('/api/payments', {
          params: {
            startDate: this.startDate,
            endDate: this.endDate,
            latestPayment: this.latestPayment || null,
          },
        })
        .then((response) => {
          // If response data is empty, do not update paymentData or latestPayment
          if (response.data.length === 0) {
            // console.log('No new payments found, keeping current data.');
            if (force) this.fetchCurrentCycleStats();
            return;
          }

          // Update paymentData and latestPayment if there are new payments
          this.paymentData = response.data.map((payment) => ({
            ...payment,
            purpose: this.paymentPurposeMap[payment.purpose] || '',
            status: this.paymentStatusMap[payment.status] || '',
            leaseBalance: this.leaseBalanceFor(payment.LID),
          }));

          // Update latestPayment to the most recent ModifiedOn value
          this.latestPayment = response.data[0].ModifiedOn;
          this.fetchCurrentCycleStats();
        })
        .catch((error) => {
          // Log error without storing details
          console.error('Error fetching payments:', error);
        });
    },

    getUncollectedCyclePeriod() {
      const { currentPeriodStart, currentPeriodEnd, nyDate } =
        getCurrentCyclePeriod();
      if (this.uncollectedLeasesCycle === 'previous') {
        const { previousPeriodStart, previousPeriodEnd } =
          getPreviousCyclePeriod(currentPeriodStart);
        return {
          periodStart: previousPeriodStart,
          periodEnd: previousPeriodEnd,
          nyDate,
        };
      }
      return {
        periodStart: currentPeriodStart,
        periodEnd: currentPeriodEnd,
        nyDate,
      };
    },

    async loadUncollectedLeasesData() {
      this.uncollectedLeasesLoading = true;

      try {
        const { periodStart, periodEnd, nyDate } =
          this.getUncollectedCyclePeriod();
        const fetchStartDate = formatDateForInput(
          new Date(nyDate.getFullYear(), nyDate.getMonth() - 2, 25),
        );
        const fetchEndDate = formatDateForInput(nyDate);
        const startDateStr = formatDateForInput(periodStart);
        const endDateStr = formatDateForInput(periodEnd);

        const [propertyRes, paymentsRes, bouncedLeases] = await Promise.all([
          axios.get('/api/property-lease-summary'),
          axios.get('/api/payments', {
            params: {
              startDate: fetchStartDate,
              endDate: fetchEndDate,
            },
          }),
          axios
            .get('/api/bounced-payments-current-cycle', {
              params: {
                startDate: startDateStr,
                endDate: endDateStr,
              },
              timeout: 8000,
            })
            .then((res) => res.data)
            .catch((err) => {
              console.warn(
                'Bounced-payment lookup failed; showing uncollected list without bounce flags:',
                err?.message || err,
              );
              return [];
            }),
        ]);

        this.uncollectedLeasesList = buildUnpaidLeasesList({
          propertySummary: propertyRes.data,
          payments: paymentsRes.data,
          bouncedPaymentLeases: bouncedLeases,
          currentPeriodStart: periodStart,
          currentPeriodEnd: periodEnd,
          currentDate: nyDate,
          mode: this.uncollectedLeasesPopupMode,
        });
      } catch (error) {
        console.error('Error loading uncollected leases:', error);
        this.uncollectedLeasesList = [];
        this.footerMessage = 'Error loading uncollected leases.';
      } finally {
        this.uncollectedLeasesLoading = false;
        this.$nextTick(() => this.constrainUncollectedPopupHeight());
      }
    },

    async openUncollectedLeasesPopup(mode = 'rent') {
      this.uncollectedLeasesPopupMode = mode === 'balance' ? 'balance' : 'rent';
      this.uncollectedBalancesSort =
        mode === 'balance' ? 'desc' : 'default';
      this.showUncollectedLeasesPopup = true;
      this.uncollectedLeasesSearch = '';
      this.uncollectedLeasesCycle = 'current';
      await this.loadUncollectedLeasesData();
    },

    async toggleUncollectedLeasesCycle() {
      this.uncollectedLeasesCycle =
        this.uncollectedLeasesCycle === 'previous' ? 'current' : 'previous';
      await this.loadUncollectedLeasesData();
    },

    cycleUncollectedBalancesSort() {
      if (this.uncollectedBalancesSort === 'default') {
        this.uncollectedBalancesSort = 'desc';
      } else if (this.uncollectedBalancesSort === 'desc') {
        this.uncollectedBalancesSort = 'asc';
      } else {
        this.uncollectedBalancesSort = 'default';
      }
    },

    async refreshUncollectedLeasesPopup() {
      await this.loadUncollectedLeasesData();
    },

    openUncollectedNotifyPopup() {
      const all = this.filteredUncollectedLeasesList.filter(Boolean);
      if (!all.length) {
        this.footerMessage = 'No unpaid tenants to notify.';
        return;
      }
      const queue = filterUncollectedNotifyQueue(all);
      this.uncollectedNotifyQueue = queue;
      this.uncollectedNotifyIndex = 0;
      this.uncollectedNotifyErrors = [];
      this.uncollectedNotifyDone = queue.length === 0;
      this.uncollectedNotifySending = false;
      this.uncollectedNotifySendAll = false;
      this.showUncollectedNotifyPopup = true;
    },

    onUncollectedNotifyOverlayClick() {
      if (!this.uncollectedNotifyBusy) {
        this.closeUncollectedNotifyPopup();
      }
    },

    closeUncollectedNotifyPopup() {
      this.showUncollectedNotifyPopup = false;
      this.uncollectedNotifySending = false;
      this.uncollectedNotifySendAll = false;
    },

    async sendUncollectedNotify() {
      if (this.uncollectedNotifySendAll) return;
      await this.sendOneUncollectedNotify();
    },

    async sendUncollectedNotifyAll() {
      if (this.uncollectedNotifyBusy || this.uncollectedNotifyDone) return;
      this.uncollectedNotifySendAll = true;
      try {
        while (
          this.uncollectedNotifySendAll &&
          this.showUncollectedNotifyPopup &&
          !this.uncollectedNotifyDone &&
          this.uncollectedNotifyCurrent
        ) {
          const ok = await this.sendOneUncollectedNotify();
          if (!ok) break;
        }
      } finally {
        this.uncollectedNotifySendAll = false;
      }
    },

    async sendOneUncollectedNotify() {
      const lease = this.uncollectedNotifyCurrent;
      if (!lease || this.uncollectedNotifySending) return false;
      if (wasUncollectedNotifySentToday(lease)) {
        this.advanceUncollectedNotify();
        return true;
      }
      if (!beginUncollectedNotifySend(lease)) {
        this.uncollectedNotifyErrors = [
          'A notification for this tenant is already in progress.',
        ];
        return false;
      }
      this.uncollectedNotifySending = true;
      this.uncollectedNotifyErrors = [];
      try {
        const { data } = await axios.post('/api/uncollected-rent-notify', {
          peopleId: lease.peopleId,
          leaseId: lease.leaseId,
          propertyId: lease.propertyId,
          emails: lease.sendEmails || lease.emails,
          tenant: lease.tenant,
          property: lease.property,
          amount: lease.rentOwed,
          balance:
            this.uncollectedLeasesPopupMode === 'balance'
              ? lease.balanceOwed
              : lease.balance,
          balanceOnly: this.uncollectedLeasesPopupMode === 'balance',
        });
        const errors = [
          ...(data?.errors || []),
          ...(data?.warnings || []),
        ];
        this.uncollectedNotifyErrors = errors;
        if (!data?.delivered) {
          if (!this.uncollectedNotifyErrors.length) {
            this.uncollectedNotifyErrors = ['Notification was not delivered.'];
          }
          return false;
        }
        markUncollectedNotifySentToday(lease);
        if (data.warnings?.length) {
          this.footerMessage = data.warnings.join(' ');
        }
        if (
          this.showUncollectedNotifyPopup &&
          this.uncollectedNotifyCurrent === lease
        ) {
          this.advanceUncollectedNotify();
        }
        return true;
      } catch (err) {
        const apiErrors = err.response?.data?.errors;
        this.uncollectedNotifyErrors = Array.isArray(apiErrors) && apiErrors.length
          ? apiErrors
          : [err.response?.data?.error || err.message || 'Send failed.'];
        return false;
      } finally {
        endUncollectedNotifySend(lease);
        this.uncollectedNotifySending = false;
      }
    },

    skipUncollectedNotify() {
      if (this.uncollectedNotifyBusy || this.uncollectedNotifyDone) return;
      this.advanceUncollectedNotify();
    },

    advanceUncollectedNotify() {
      const next = this.uncollectedNotifyIndex + 1;
      if (next >= this.uncollectedNotifyQueue.length) {
        this.uncollectedNotifyDone = true;
        this.uncollectedNotifyErrors = [];
        return;
      }
      this.uncollectedNotifyIndex = next;
      this.uncollectedNotifyErrors = [];
    },

    openLeaseStateFromUncollected(lease) {
      const leaseId = lease?.leaseId;
      if (leaseId == null || leaseId === '') {
        this.footerMessage = 'No lease id for this row; cannot edit lease status.';
        return;
      }
      this.selectedLeaseStateLeaseId = Number(leaseId);
      this.showLeaseStateDialog = true;
    },

    openEmailFromUncollected(lease) {
      const emailList = String(lease?.emails || '')
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean)
        .join(', ');
      if (!emailList) {
        this.footerMessage = 'No email addresses for this tenant';
        return;
      }
      // Same path as double-click Email column on the payments table
      this.openEmailEditor(emailList, '', '');
    },

    onLeaseStateDialogVisibility(visible) {
      this.showLeaseStateDialog = visible;
      if (!visible) {
        const leaseId = this.selectedLeaseStateLeaseId;
        this.selectedLeaseStateLeaseId = null;
        // Refresh unpaid list so LStat column reflects edits
        if (this.showUncollectedLeasesPopup) {
          this.refreshUncollectedLeasesPopup();
        }
        if (leaseId != null) {
          this.refreshLeaseStateOnPayments(leaseId);
        }
      }
    },

    leaseStateTitle(payment) {
      const desc =
        payment?.leaseStateDescription ||
        'Lease state closest to this payment date';
      const date = payment?.leaseStateDate
        ? `\n${payment.leaseStateDate}`
        : '';
      const edit = payment?.LID
        ? '\n\ndouble-click to edit lease status'
        : '';
      return `${desc}${date}${edit}`;
    },

    openLeaseStateFromPayment(payment) {
      const leaseId = payment?.LID ?? payment?.leaseId;
      if (leaseId == null || leaseId === '') {
        this.footerMessage = 'No lease id for this row; cannot edit lease status.';
        return;
      }
      this.selectedLeaseStateLeaseId = Number(leaseId);
      this.showLeaseStateDialog = true;
    },

    leaseStatePatchFromClosest(states, types, paymentDate) {
      const empty = {
        leaseState: '',
        leaseStateDescription: '',
        leaseStateForeground: '',
        leaseStateBackground: '',
        leaseStateDate: '',
      };
      if (!states?.length) return empty;
      const payTime = paymentDate ? new Date(paymentDate).getTime() : NaN;
      let closest = states[0];
      if (!Number.isNaN(payTime)) {
        let bestDiff = Infinity;
        for (const state of states) {
          const stateTime = new Date(state.ModifiedOn).getTime();
          if (Number.isNaN(stateTime)) continue;
          const diff = Math.abs(stateTime - payTime);
          if (diff < bestDiff) {
            bestDiff = diff;
            closest = state;
          }
        }
      }
      const type = (types || []).find((t) => t.shortCode === closest.shortCode);
      let leaseStateDate = '';
      if (closest.ModifiedOn) {
        const d = new Date(closest.ModifiedOn);
        if (!Number.isNaN(d.getTime())) {
          leaseStateDate = d.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            timeZone: 'America/New_York',
          });
        }
      }
      return {
        leaseState: closest.shortCode || '',
        leaseStateDescription: closest.Description || '',
        leaseStateForeground: type?.foreground || '',
        leaseStateBackground: type?.background || '',
        leaseStateDate,
      };
    },

    async refreshLeaseStateOnPayments(leaseId) {
      if (leaseId == null) return;
      try {
        const [statesRes, typesRes] = await Promise.all([
          axios.get(`/api/lease/${leaseId}/states`),
          axios.get('/api/lease-state-types?useFor=1'),
        ]);
        const states = statesRes.data || [];
        const types = typesRes.data || [];
        this.paymentData = this.paymentData.map((p) => {
          if (Number(p.LID) !== Number(leaseId)) return p;
          return {
            ...p,
            ...this.leaseStatePatchFromClosest(
              states,
              types,
              p.paidOn || p.effectiveDate,
            ),
          };
        });
      } catch (error) {
        console.error('Error refreshing lease state on payments:', error);
      }
    },

    printUncollectedLeasesList() {
      const list = this.filteredUncollectedLeasesList;
      if (!list.length) {
        return;
      }

      const isBalance = this.uncollectedLeasesPopupMode === 'balance';
      const title = isBalance
        ? 'Unpaid Balances — Current Cycle'
        : this.uncollectedLeasesCycle === 'previous'
          ? 'Uncollected Rent — Previous Cycle'
          : 'Uncollected Rent — Current Cycle';
      const amountHeader = isBalance ? 'Balance' : 'Rent';
      const showLedgerBalance = !isBalance;

      const { periodStart, periodEnd } = this.getUncollectedCyclePeriod();
      const cycleStart = this.formatDate(periodStart);
      const cycleEnd = this.formatDate(periodEnd);
      const printedOn = new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
      });
      const total = this.uncollectedLeasesPopupListTotal;

      const rows = list
        .map((lease) => {
          const bounceNote = lease.bouncedPayment
            ? `Bounced ${lease.bounceAmount || ''} on ${lease.bounceDate || ''}`.trim()
            : '';
          const amount = isBalance ? lease.balanceOwed : lease.rentOwed;
          const bal = Number(lease.balanceValue);
          const balColor =
            bal > 0 ? '#1B5E20' : bal < 0 ? '#B71C1C' : 'inherit';
          const balanceCell = showLedgerBalance
            ? `<td class="num" style="color:${balColor}">${escapePrintHtml(lease.balance)}</td>`
            : '';
          return `<tr>
            <td>${escapePrintHtml(lease.property)}</td>
            <td>${escapePrintHtml(lease.tenant)}</td>
            <td>${escapePrintHtml(lease.dueDate)}</td>
            <td class="num">${escapePrintHtml(amount)}</td>
            ${balanceCell}
            <td class="center">${escapePrintHtml(lease.leaseState)}</td>
            <td>${escapePrintHtml(bounceNote)}</td>
          </tr>`;
        })
        .join('');

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>${escapePrintHtml(title)}</title>
            <style>
              @page {
                size: letter portrait;
                margin: 0.4in 0.45in;
              }
              html,
              body {
                width: 100%;
              }
              body {
                font-family: Arial, Helvetica, sans-serif;
                font-size: 8.5pt;
                line-height: 1.25;
                color: #000;
                margin: 0;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              h1 {
                font-size: 12.5pt;
                margin: 0 0 4px;
                font-weight: 700;
              }
              .meta {
                font-size: 8pt;
                color: #444;
                margin-bottom: 8px;
                line-height: 1.3;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
              }
              th,
              td {
                border-bottom: 1px solid #ccc;
                padding: 2px 4px;
                text-align: left;
                vertical-align: top;
                word-wrap: break-word;
                overflow-wrap: anywhere;
              }
              th {
                font-size: 7.5pt;
                background: #f2f2f2;
                text-transform: uppercase;
                letter-spacing: 0.02em;
              }
              /* Portrait-friendly column widths */
              th:nth-child(1),
              td:nth-child(1) {
                width: ${showLedgerBalance ? '24%' : '28%'};
              }
              th:nth-child(2),
              td:nth-child(2) {
                width: ${showLedgerBalance ? '20%' : '24%'};
              }
              th:nth-child(3),
              td:nth-child(3) {
                width: 11%;
              }
              th:nth-child(4),
              td:nth-child(4) {
                width: 11%;
              }
              th:nth-child(5),
              td:nth-child(5) {
                width: ${showLedgerBalance ? '11%' : '8%'};
              }
              th:nth-child(6),
              td:nth-child(6) {
                width: ${showLedgerBalance ? '8%' : '18%'};
              }
              th:nth-child(7),
              td:nth-child(7) {
                width: 15%;
              }
              .num {
                text-align: right;
                white-space: nowrap;
              }
              .center {
                text-align: center;
              }
              tfoot td {
                font-weight: bold;
                border-top: 2px solid #333;
                border-bottom: none;
                padding-top: 5px;
              }
              tfoot .num {
                text-align: right;
              }
            </style>
          </head>
          <body>
            <h1>${escapePrintHtml(title)}</h1>
            <div class="meta">
              Cycle: ${escapePrintHtml(cycleStart)} – ${escapePrintHtml(cycleEnd)}<br />
              Printed: ${escapePrintHtml(printedOn)} ET<br />
              Leases: ${list.length}
            </div>
            <table>
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Tenant</th>
                  <th>Due</th>
                  <th class="num">${escapePrintHtml(amountHeader)}</th>
                  ${showLedgerBalance ? '<th class="num">Balance</th>' : ''}
                  <th class="center">LStat</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3">Grand Total</td>
                  <td class="num">${escapePrintHtml(total)}</td>
                  <td colspan="${showLedgerBalance ? '3' : '2'}"></td>
                </tr>
              </tfoot>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    },

    closeUncollectedLeasesPopup() {
      this.showUncollectedLeasesPopup = false;
      const el = this.$refs.uncollectedPopupContent;
      if (el) {
        el.style.height = '';
        el.style.maxHeight = '';
        el.style.bottom = '';
      }
    },

    constrainUncollectedPopupHeight() {
      const el = this.$refs.uncollectedPopupContent;
      if (!el) return;

      const edgeGap = 16;
      const viewport = window.visualViewport;
      const viewportHeight = viewport?.height ?? window.innerHeight;
      const viewportTop = viewport?.offsetTop ?? 0;
      const viewportWidth = viewport?.width ?? window.innerWidth;

      el.style.bottom = '';
      el.style.height = '85vh';
      el.style.maxHeight = '85vh';

      const popupHeight = el.getBoundingClientRect().height;
      let top = parseFloat(el.style.top);
      if (Number.isNaN(top)) {
        top = 90;
      }

      const maxTop = viewportTop + viewportHeight - popupHeight - edgeGap;
      top = Math.min(Math.max(top, viewportTop + edgeGap), maxTop);
      el.style.top = `${top}px`;

      let left = parseFloat(el.style.left);
      if (Number.isNaN(left)) {
        left = 180;
      }
      const width = el.offsetWidth || 440;
      const maxLeft = viewportWidth - width - edgeGap;
      left = Math.min(Math.max(left, edgeGap), maxLeft);
      el.style.left = `${left}px`;
    },

    initUncollectedPopupDrag() {
      if (this.uncollectedPopupDragCleanup) {
        this.uncollectedPopupDragCleanup();
        this.uncollectedPopupDragCleanup = null;
      }
      const el = this.$refs.uncollectedPopupContent;
      if (!el) return;
      this.uncollectedPopupDragCleanup = makeDraggable(el, {
        handleSelector: '.uncollected-popup-drag-handle',
        storageKeyPrefix: 'paymentViewerUncollectedPopup',
        onPositionChange: () => this.constrainUncollectedPopupHeight(),
      });
      this.constrainUncollectedPopupHeight();
    },

    handleUncollectedPopupResize() {
      if (this.showUncollectedLeasesPopup) {
        this.constrainUncollectedPopupHeight();
      }
    },

    /**
     * Multi-select payments that make up "clearing": same set whose amounts
     * sum to the Clearing cycle-stat (current + previous cycle, undeposited).
     */
    async selectClearingPayments() {
      try {
        const {
          previousPeriodStart,
          nyDate,
        } = getClearingCycleContext();

        const rangeStartStr = formatDateForInput(previousPeriodStart);
        const rangeEndStr = formatDateForInput(nyDate);

        // Ensure the table date range covers previous + current cycles
        let needRefetch = false;
        if (!this.startDate || this.startDate > rangeStartStr) {
          this.startDate = rangeStartStr;
          needRefetch = true;
        }
        if (!this.endDate || this.endDate < rangeEndStr) {
          this.endDate = rangeEndStr;
          needRefetch = true;
        }

        // Clear search/filters that would hide clearing rows
        this.inputQuery = '';
        this.searchQuery = '';
        this.filterForChecks = false;
        this.filterForEchecks = false;
        this.filterForUngrouped = false;
        this.filterForGrouped = false;
        this.filterForEmptyAddress = false;

        if (needRefetch || !this.paymentData?.length) {
          await this.fetchPayments(true);
        }

        const clearingPayments = filterClearingPayments(this.paymentData);

        this.selectedPayments = clearingPayments;
        this.lastSelectedIndex =
          clearingPayments.length > 0
            ? this.filteredPayments.findIndex(
                (p) => p.id === clearingPayments[0].id,
              )
            : null;
        localStorage.setItem(
          'selectedPayments',
          JSON.stringify(this.selectedPayments),
        );

        const sum = clearingPayments.reduce(
          (s, p) => s + parseFloat(p.amount || 0),
          0,
        );
        this.footerMessage =
          clearingPayments.length === 0
            ? 'No undeposited payments in the current or previous cycle (clearing).'
            : `Selected ${clearingPayments.length} clearing payment(s) from current/previous cycle totaling ${formatCycleCurrencyExact(sum)}.`;

        // Scroll first selected row into view when present in the filtered table
        this.$nextTick(() => {
          const first = this.$el?.querySelector?.(
            '.table-body tbody tr.selected-row',
          );
          if (first && typeof first.scrollIntoView === 'function') {
            first.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        });
      } catch (error) {
        console.error('Error selecting clearing payments:', error);
        this.footerMessage = 'Could not select clearing payments.';
      }
    },

    async fetchCurrentCycleStats() {
      try {
        const { currentPeriodStart, currentPeriodEnd, nyDate } =
          getCurrentCyclePeriod();
        const currentMonth = nyDate.getMonth();
        const currentYear = nyDate.getFullYear();

        const fetchStartDate = formatDateForInput(
          new Date(currentYear, currentMonth - 2, 25),
        );
        const fetchEndDate = formatDateForInput(nyDate);

        const [propertyRes, paymentsRes, depositsRes, vacantRes] =
          await Promise.all([
            axios.get('/api/property-lease-summary'),
            axios.get('/api/payments', {
              params: {
                startDate: fetchStartDate,
                endDate: fetchEndDate,
              },
            }),
            axios.get('/api/current-cycle-deposits'),
            axios.get('/api/vacant-rent-loss'),
          ]);

        const totalProperties = propertyRes.data.length;
        const paidInCurrentPeriod = paymentsRes.data.filter(
          (payment) =>
            isPaymentInCycle(
              payment,
              currentPeriodStart,
              currentPeriodEnd,
              nyDate,
            ) && isCountableCyclePayment(payment),
        );

        const uniqueCurrentPeriodAddresses = new Set(
          paidInCurrentPeriod.map((payment) => payment.address),
        );
        const paymentCount = uniqueCurrentPeriodAddresses.size;
        // Share of cycle payment rows received via PixieChatBot (mobile icon)
        const mobilePaymentCount = paidInCurrentPeriod.filter(
          (payment) => payment.paidBy === 'chatBot',
        ).length;
        const propertiesPct = totalProperties
          ? Math.round((paymentCount / totalProperties) * 100)
          : 0;
        const mobilePct = paidInCurrentPeriod.length
          ? Math.round((mobilePaymentCount / paidInCurrentPeriod.length) * 100)
          : 0;
        const paymentPercent = `(${propertiesPct}%,${mobilePct}%)`;
        const paymentSum = paidInCurrentPeriod.reduce((sum, payment) => {
          return sum + parseFloat(payment.amount || 0);
        }, 0);
        const depositedSum = parseFloat(depositsRes.data.totalDeposits || 0);
        // Clearing = sum of undeposited countable payments (current + previous cycle)
        // — same set multi-selected on double-click of the Clearing stat
        const clearingSum = sumClearingPayments(paymentsRes.data);
        const cycleStatsInput = {
          propertySummary: propertyRes.data,
          payments: paymentsRes.data,
          currentPeriodStart,
          currentPeriodEnd,
          currentDate: nyDate,
        };
        const uncollectedRent = calculateUncollectedRent(cycleStatsInput);
        const unpaidBalances = calculateUnpaidBalancesTotal(cycleStatsInput);
        const vacantRentLoss = parseFloat(vacantRes.data.vacantRentLoss || 0);

        this.ingestPropertyLeaseBalances(propertyRes.data);

        this.currentCycleStats = {
          paymentCount,
          paymentPercent,
          received: formatCycleCurrency(paymentSum),
          deposited: formatCycleCurrency(depositedSum),
          clearing: formatCycleCurrencyExact(clearingSum),
          uncollected: formatCycleCurrency(uncollectedRent),
          balances: formatCycleCurrency(unpaidBalances),
          vacant: formatCycleCurrency(vacantRentLoss),
        };
      } catch (error) {
        console.error('Error fetching current cycle stats:', error);
      }
    },

    formatDate(dateString) {
      const options = {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        timeZone: 'America/New_York', // Eastern Time (automatically handles EST/EDT)
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    formatNumber(value, digits) {
      if (typeof value !== 'number') value = parseFloat(value);
      if (isNaN(value)) return '';
      if (value === 0.0) return '0.00';
      const sign = value < 0 ? '- ' : '';
      return `${sign}${Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })}`;
    },

    getBalance() {
      this.applyLeaseBalancesToPayments();
    },

    leaseBalanceKey(leaseId) {
      if (leaseId == null || leaseId === '') return null;
      return String(leaseId);
    },

    leaseBalanceFor(leaseId) {
      const key = this.leaseBalanceKey(leaseId);
      if (!key) return null;
      const value = this.leaseBalanceByLeaseId[key];
      return value == null || Number.isNaN(value) ? null : value;
    },

    ingestPropertyLeaseBalances(rows) {
      const map = {};
      for (const row of rows || []) {
        const key = this.leaseBalanceKey(row.leaseId);
        if (!key) continue;
        const value = parseFloat(row.balance);
        if (!Number.isNaN(value)) {
          map[key] = value;
        }
      }
      this.leaseBalanceByLeaseId = map;
      this.applyLeaseBalancesToPayments();
    },

    applyLeaseBalancesToPayments() {
      if (!this.paymentData.length) return;
      this.paymentData = this.paymentData.map((payment) => ({
        ...payment,
        leaseBalance: this.leaseBalanceFor(payment.LID),
      }));
    },

    formatLeaseBalance(value) {
      if (value == null || value === '') return '';
      return this.formatNumber(value, 2);
    },

    leaseBalanceColor(value) {
      if (value == null || Number.isNaN(Number(value))) return undefined;
      if (value < 0) return '#B71C1C';
      if (value > 0) return '#1B5E20';
      return undefined;
    },

    leaseBalanceTitle(value) {
      if (value == null || Number.isNaN(Number(value))) {
        return 'Current lease balance';
      }
      if (value < 0) return 'Current lease balance (owes)';
      if (value > 0) return 'Current lease balance (credit)';
      return 'Current lease balance (zero)';
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortOrder = 'asc';
      }
      this.sortValues();
    },

    async sortValues() {
      this.paymentData.sort((a, b) => {
        const sortMultiplier = this.sortOrder === 'asc' ? 1 : -1;
        const field = this.sortField;

        let aValue = a[field];
        let bValue = b[field];

        // Check if the values are dates in "mm/dd/yy" format
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const dateRegex = /^\d{2}\/\d{2}\/\d{2}$/;
          if (dateRegex.test(aValue) && dateRegex.test(bValue)) {
            // Parse as dates
            aValue = new Date(aValue);
            bValue = new Date(bValue);
          } else if (!isNaN(aValue) && !isNaN(bValue)) {
            // Parse as numbers
            aValue = parseFloat(aValue);
            bValue = parseFloat(bValue);
          }
        }

        if (aValue > bValue) return 1 * sortMultiplier;
        if (aValue < bValue) return -1 * sortMultiplier;
        return 0;
      });
    },

    selectRow(event, payment, index) {
      this.footerMessage =
        'Tip: CTRL and shift click for multiple; double click once to find more payments from this person; double click again to search people';

      // Check if the click is in a td with class "td-purpose"
      const targetElement = event.target;
      if (targetElement.classList.contains('td-depositGroup')) {
        if (payment.depositGroup) {
          this.inputQuery = payment.depositGroup?.slice(-8);
          // add each row of filteredPayments to selectedPayments
          // after a suitable delay
          this.clickTimeout = setTimeout(() => {
            this.selectedPayments = this.filteredPayments.filter(
              (p) => p.depositGroup === payment.depositGroup,
            );
          }, 1000);
          this.selectedPayments = this.filteredPayments.filter(
            (p) => p.depositGroup === payment.depositGroup,
          );
        } else {
          // If depositGroup is blank, just select this payment
          this.selectedPayments = [payment];
        }
        return;
      }
      if (targetElement.classList.contains('td-purpose')) {
        this.editPurpose(payment.id);
        return;
      }
      if (targetElement.classList.contains('td-status')) {
        this.editStatus(payment.id);
        return;
      }

      // Check if the shift key is pressed (shift-click functionality)
      if (event.shiftKey && this.lastSelectedIndex !== null) {
        const start = Math.min(this.lastSelectedIndex, index);
        const end = Math.max(this.lastSelectedIndex, index);

        for (let i = start; i <= end; i++) {
          if (!this.selectedPayments.includes(this.filteredPayments[i])) {
            this.selectedPayments.push(this.filteredPayments[i]);
          }
        }
      }
      // Check if the ctrl key (or cmd key on Mac) is pressed (ctrl-click functionality)
      else if (event.ctrlKey || event.metaKey) {
        const paymentIndex = this.selectedPayments.indexOf(payment);

        if (paymentIndex === -1) {
          this.selectedPayments.push(payment); // Add to selection
        } else {
          this.selectedPayments.splice(paymentIndex, 1); // Remove from selection
        }
      }
      // Regular click, no special keys
      else {
        this.selectedPayments = [payment]; // Replace with the clicked row
      }

      // Store the last selected index for shift-click behavior
      this.lastSelectedIndex = index;

      // Update local storage or any other related behavior
      localStorage.setItem(
        'selectedPayments',
        JSON.stringify(this.selectedPayments),
      );
    },

    async onDateChange() {
      // Clear any existing debounce timer
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      // Set a new debounce timer
      this.debounceTimeout = setTimeout(() => {
        // Check if both startDate and endDate are valid dates
        const startDate = new Date(this.startDate);
        const endDate = new Date(this.endDate);

        if (
          !this.startDate ||
          !this.endDate ||
          isNaN(startDate.getTime()) ||
          isNaN(endDate.getTime())
        ) {
          return; // Exit the function if either date is invalid
        }

        // Check if startDate is earlier than endDate
        if (startDate >= endDate) {
          return; // Exit the function if startDate is not earlier than endDate
        }

        // If validation passes, fetch payments
        this.latestPayment = null;
        this.fetchPayments();
      }, 500); // Adjust the debounce delay (in milliseconds) as needed
    },

    async handleKeyDown(event) {
      // if the 'R' key is pressed:
      // iterate over this.selectedPayments and set this.selectedPayments[i].purpose to 1 if it is = ''
      if (event.key === 'r') {
        // Filter payments that need updating (only those with empty purpose)
        const paymentsToUpdate = this.selectedPayments.filter(
          (payment) => payment.purpose === '',
        );

        if (paymentsToUpdate.length === 0) {
          return; // Nothing to update
        }

        // Optimistically update UI immediately for all selected payments
        paymentsToUpdate.forEach((payment) => {
          payment.purpose = 'Rent';

          // Also update in paymentData to ensure consistency
          const paymentIndex = this.paymentData.findIndex(
            (p) => p.id === payment.id,
          );
          if (paymentIndex !== -1) {
            this.paymentData[paymentIndex].purpose = 'Rent';
          }
        });

        // Batch update all payments in a single API call
        const paymentIds = paymentsToUpdate.map((payment) => payment.id);
        this.updatePurposeBatch(paymentIds, 1); // 1 = Rent
      }
      // If the Escape key is pressed
      if (event.key === 'Escape') {
        if (this.selectedPayments.length === 0) {
          // If there are no selected payments, clear the search query
          this.selectedPayment = null;
          this.searchQuery = '';
          this.inputQuery = '';
        } else {
          // Otherwise, clear the selected payments
          this.selectedPayments = [];
        }
        if (this.$refs.searchQueryInput) this.$refs.searchQueryInput.focus();
        event.preventDefault();
      }
    },

    openEmailEditor(
      to = 'dan@bigpropertymanagement.com',
      subject = '',
      body = '<br><br>**Pixie Properties Team**<br>616-916-6953',
    ) {
      eventBus.emit('openQuickEmail', { to, subject, body });
    },

    isEmailAddress(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());
    },

    openNewPersonDialog(email) {
      eventBus.emit('openNewPersonIframe', {
        email,
        title: email,
      });
    },

    handleDoubleClick(event, payment) {
      const clickedElement = event.target;

      if (
        clickedElement.classList.contains('lease-state') ||
        clickedElement.closest('.lease-state')
      ) {
        this.openLeaseStateFromPayment(payment);
        event.preventDefault();
        return;
      }

      if (
        clickedElement.classList.contains('address') ||
        clickedElement.closest('.address')
      ) {
        this.navigateToLeaseSearch(payment.address);
        event.preventDefault();
        return;
      }

      // Handle double-click on DepId column - drill through to /main to find the deposit
      if (
        clickedElement.classList.contains('td-depositGroup') ||
        clickedElement.closest('.td-depositGroup')
      ) {
        if (payment.depositGroup) {
          // Use last 8 chars to match what's displayed in the table
          const searchValue = payment.depositGroup.slice(-8);
          const url = this.$router.resolve({
            path: '/main',
            query: {
              page: 'bank',
              q: searchValue,
            },
          }).href;
          window.open(url, '_blank');
          event.preventDefault();
        }
        return;
      }

      if (
        (clickedElement.classList.contains('fullName') ||
          clickedElement.closest('.fullName')) &&
        this.isEmailAddress(payment.fullName)
      ) {
        this.openNewPersonDialog(payment.fullName);
        event.preventDefault();
        return;
      }

      if (this.searchQuery === this.selectedPayments[0]?.fullName) {
        eventBus.emit('openTenantSearchIframe', {
          search: payment.Email,
          title: payment.fullName,
        });
      } else {
        // Check if the clicked element is within the name
        if (clickedElement.classList.contains('fullName')) {
          this.inputQuery = payment.fullName;
          this.searchQuery = payment.fullName;
          const currentYear = new Date().getFullYear();
          this.startDate = new Date(`${currentYear}-01-01`)
            .toISOString()
            .split('T')[0]; // Convert to YYYY-MM-DD
          this.selectedPayment = payment;
          console.log('here:', this.selectedPayment);
          this.fetchPayments();
          event.preventDefault();
          return;
        }
        if (
          clickedElement.classList.contains('Email') ||
          clickedElement.closest('.Email')
        ) {
          if (event.shiftKey) {
            // Shift+double-click: open People Search on the send address.
            if (payment.Email) {
              this.$router.push({
                path: '/people',
                query: { search: payment.Email },
              });
            }
          } else if (payment.Email) {
            this.openEmailEditor(payment.Email, '', '');
          }
          event.preventDefault();
          return;
        }
        if (clickedElement.classList.contains('transactionNumber')) {
          // Open email editor for failed payment notification
          const subject = `Failed Payment Notification - Transaction #${payment.transactionNumber}`;
          const body = `Dear ${
            payment.fullName
          },<br><br>We wanted to notify you that your recent payment for ${
            payment.address
          } has encountered an issue.<br><br>Transaction Details:<br>• Transaction Number: ${
            payment.transactionNumber
          }<br>• Amount: $${payment.amount}<br>• Date: ${this.formatDate(
            payment.paidOn,
          )}<br>• Status: ${
            payment.status
          }<br><br>Please resubmit payment at your earliest convenience to avoid additional fees.<br><br>**Pixie Properties Team**<br>616-916-6953`;

          if (payment.Email) {
            this.openEmailEditor(payment.Email, subject, body);
          }
          event.preventDefault();
        }
      }
    },

    navigateToLeaseSearch(address) {
      eventBus.emit('openLeaseSearchIframe', address);
    },

    async fetchUnreadCounts() {
      try {
        const response = await axios.get('/api/checkUnreadState');
        this.unreadReceipts = response.data.receipts;
      } catch (error) {
        console.error('Failed to fetch unread counts:', error);
        // Handle error appropriately
      }
    },

    editPurpose(paymentId) {
      this.editingPurpose = paymentId;
      this.openSelectDropdown();
    },

    openSelectDropdown() {
      this.$nextTick(() => {
        const selectElement = this.$refs.autoOpenSelect[0];
        // this is an attempt to auto-open the select, but it doesn't work
        if (selectElement) {
          // Set selectedIndex to -1 to ensure no option is selected
          selectElement.selectedIndex = -1;

          // Focus on the select element
          selectElement.focus();
          this.$nextTick(() => {
            // Optionally, if there's a native way to open, use it
            // For example, if the select has an `open` method or property
            if (selectElement.open) {
              selectElement.open = true;
            } else {
              // Fallback to event simulation if direct method not available
              setTimeout(() => {
                const clickEvent = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window,
                });
                selectElement.dispatchEvent(clickEvent);
              }, 0);
            }
          });
        }
      });
    },

    async updatePurpose(payment) {
      try {
        await axios.post('/api/payment-purpose', {
          paymentId: payment.id,
          purpose: this.selectedPurpose,
        });
        payment.purpose = this.paymentPurposeMap[this.selectedPurpose];
        this.editingPurpose = null; // Close the editor
        this.selectedPurpose = null;
      } catch (error) {
        console.error('Error updating purpose:', error);
      }
    },

    editStatus(paymentId) {
      this.editingStatus = paymentId;
      this.openStatusSelectDropdown();
    },

    openStatusSelectDropdown() {
      this.$nextTick(() => {
        const selectElement = this.$refs.autoOpenStatusSelect?.[0];
        if (selectElement) {
          selectElement.selectedIndex = -1;
          selectElement.focus();
          this.$nextTick(() => {
            if (selectElement.open) {
              selectElement.open = true;
            } else {
              setTimeout(() => {
                const clickEvent = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window,
                });
                selectElement.dispatchEvent(clickEvent);
              }, 0);
            }
          });
        }
      });
    },

    async updateStatus(payment) {
      try {
        await axios.post('/api/payment-status', {
          paymentId: payment.id,
          status: this.selectedStatus,
        });
        payment.status = this.paymentStatusMap[this.selectedStatus];
        this.editingStatus = null;
        this.selectedStatus = null;
      } catch (error) {
        console.error('Error updating status:', error);
      }
    },

    async updatePurposeBatch(paymentIds, purpose) {
      try {
        await axios.post('/api/payment-purpose-batch', {
          paymentIds: paymentIds,
          purpose: purpose,
        });
        // No need to update UI here - already done optimistically
        // Socket updates will confirm the changes
      } catch (error) {
        console.error('Error updating purposes:', error);
        // On error, refetch to get correct state
        this.fetchPayments();
      }
    },

    toggleFilterForChecks() {
      this.filterForChecks = !this.filterForChecks;
      if (this.filterForChecks) {
        this.filterForEchecks = false;
      }
    },

    toggleFilterForEchecks() {
      this.filterForEchecks = !this.filterForEchecks;
      if (this.filterForEchecks) {
        this.filterForChecks = false;
      }
    },

    toggleFilterForUngrouped() {
      this.filterForGrouped = false;
      this.filterForUngrouped = !this.filterForUngrouped;
    },

    toggleFilterForGrouped() {
      this.filterForUngrouped = false;
      this.filterForGrouped = !this.filterForGrouped;
    },

    toggleFilterForEmptyAddress() {
      this.filterForEmptyAddress = !this.filterForEmptyAddress;
    },

    groupIntoDeposit() {
      // only do this if all selectedPayments have no depositGroup AND if all selectedPayments do not have payment.method = 2
      if (
        this.selectedPayments?.length === 0 ||
        this.selectedPayments.some(
          (payment) => payment.depositGroup && payment.depositGroup !== '',
        ) ||
        this.selectedPayments.some((payment) => payment.method === 2)
      ) {
        this.footerMessage =
          'Please select ungrouped, non-electronic payments to group.';
        return;
      }

      // Find the newest date from selectedPayments.paidOn
      const paidOnDates = this.selectedPayments.map(
        (payment) => new Date(payment.paidOn),
      );
      const newestDate = new Date(
        Math.max(...paidOnDates.map((date) => date.getTime())),
      );

      // Format the newest date
      const formattedDate = newestDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
      });
      const depositGroupBase = formattedDate.replace(/\//g, '');

      // Find the next available .N for the depositGroup
      let nextSuffix = 1;
      let depositGroupWithDot;
      do {
        depositGroupWithDot = `${depositGroupBase}.${nextSuffix}`;
        nextSuffix++;
      } while (
        this.paymentData.some(
          (payment) => payment.depositGroup === depositGroupWithDot,
        )
      );

      // Rest of the function remains the same...
      // Update selectedPayments and paymentData with the new depositGroup
      this.selectedPayments.forEach((payment) => {
        const paymentIndex = this.paymentData.findIndex(
          (p) => p.id === payment.id,
        );
        if (paymentIndex !== -1) {
          this.paymentData[paymentIndex].depositGroup = depositGroupWithDot;
        }
      });
      this.selectedPayments.forEach((payment) => {
        payment.depositGroup = depositGroupWithDot;
      });

      // Call an API to update the depositGroup for each payment
      const paymentIds = this.selectedPayments.map((payment) => payment.id);
      axios
        .post('/api/update-deposit-group', {
          paymentIds: paymentIds,
          depositGroup: depositGroupWithDot,
        })
        .then(() => {
          console.log('Deposit group updated successfully');
        })
        .catch((error) => {
          console.error('Error updating deposit group:', error);
        });

      this.footerMessage = `Grouped ${this.selectedPayments.length} payments into deposit group ${depositGroupWithDot}`;
    },
    // input looks like: 2025-04-05T21:40:49.079Z  output should be: 04/05/25, 21:40:49
    formatDateTime(dateString) {
      if (!dateString || isNaN(new Date(dateString).getTime())) {
        return ''; // Return empty string if dateString is invalid or undefined
      }
      const options = {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'America/New_York', // Eastern Time (automatically handles EST/EDT)
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },

    isFailedAmountStatus(payment) {
      const status = payment?.status;
      return (
        status === 'Rejected' ||
        status === 'Returned' ||
        status === 'Voided'
      );
    },

    getStatusColor(payment) {
      // If payment has a depositGroup, return the color for complete/funded/settled
      if (
        payment.depositGroup &&
        payment.depositGroup !== null &&
        payment.depositGroup !== ''
      ) {
        return { text: '#006400', background: '#E8F5E9' }; // Green theme
      }

      // Otherwise, use the original status-based logic
      const status = payment.status;
      switch (status) {
        case 'ready':
          return { text: '#0D47A1', background: '#E3F2FD' }; // Pale blue theme
        case 'complete':
        case 'funded':
        case 'settled':
          return { text: '#006400', background: '#E8F5E9' }; // Green theme
        case 'settling':
          return { text: '#E65100', background: '#FFF3E0' }; // Orange theme
        case 'declined':
        case 'rejected':
        case 'unfunded':
          return { text: '#B71C1C', background: '#FFEBEE' }; // Red theme
        case 'voided':
          return { text: '#616161', background: '#EEEEEE' }; // Grey theme
        default:
          return { text: null, background: null }; // Default
      }
    },

    openPaymentStatusesModal() {
      // Open modal and fetch the markdown file
      this.showPaymentStatusesModal = true;
      axios
        .get(window.location.origin + '/payment_statuses.md')
        .then((response) => {
          this.paymentStatusesHtml = marked.parse(response.data);
        })
        .catch((error) => {
          console.error('Error loading payment statuses:', error);
          this.paymentStatusesHtml = '<p>Error loading payment statuses</p>';
        });
    },

    closePaymentStatusesModal() {
      this.showPaymentStatusesModal = false;
    },

    formatRelativeTime(dateString) {
      if (!dateString || isNaN(new Date(dateString).getTime())) {
        return 'never'; // Return 'never' if dateString is invalid or undefined
      }

      const now = new Date();
      const date = new Date(dateString);
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      // Return the appropriate relative time string
      if (diffSecs < 60) {
        return `${diffSecs} sec${diffSecs !== 1 ? 's' : ''} ago`;
      } else if (diffMins < 60) {
        return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hr${diffHours !== 1 ? 's' : ''} ago`;
      } else if (diffDays < 7) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      } else {
        // For older dates, show the original date format
        return this.formatDateTime(dateString);
      }
    },
    clearDeposit() {
      // Check if any payments are selected
      if (this.selectedPayments.length === 0) {
        this.footerMessage =
          'Please select payments to remove from deposit group.';
        return;
      }

      // Check if all selected payments have a deposit group assigned
      const hasNoDepositGroup = this.selectedPayments.some(
        (payment) => !payment.depositGroup || payment.depositGroup === '',
      );

      if (hasNoDepositGroup) {
        this.footerMessage =
          'Some selected payments are not in a deposit group.';
        return;
      }

      // Update selectedPayments and paymentData with empty depositGroup
      this.selectedPayments.forEach((payment) => {
        // Update in paymentData
        const paymentIndex = this.paymentData.findIndex(
          (p) => p.id === payment.id,
        );
        if (paymentIndex !== -1) {
          this.paymentData[paymentIndex].depositGroup = '';
        }
        // Update in selectedPayments
        payment.depositGroup = '';
      });

      // Call the API to update the depositGroup for each payment
      const paymentIds = this.selectedPayments.map((payment) => payment.id);
      axios
        .post('/api/update-deposit-group', {
          paymentIds: paymentIds,
          depositGroup: '', // Empty string to clear the deposit group
        })
        .then(() => {
          console.log('Deposit group cleared successfully');
        })
        .catch((error) => {
          console.error('Error clearing deposit group:', error);
        });

      this.footerMessage = `Removed ${this.selectedPayments.length} payments from their deposit group`;
    },

    editDepositGroup(event) {
      // Check if any payments are selected
      if (this.selectedPayments.length === 0) {
        this.footerMessage = 'Please select payments to edit deposit group.';
        return;
      }

      // Get the first payment's depositGroup
      const firstDepositGroup = this.selectedPayments[0].depositGroup;

      // Check if all selected payments have the same depositGroup
      const allSameGroup = this.selectedPayments.every(
        (payment) => payment.depositGroup === firstDepositGroup,
      );

      if (!allSameGroup) {
        this.footerMessage =
          'All selected payments must have the same deposit group to edit.';
        return;
      }

      // Capture click position
      const clickX = event.clientX;
      const clickY = event.clientY;
      const viewportHeight = window.innerHeight;
      const isTopHalf = clickY < viewportHeight / 2;

      this.modalPosition = {
        x: clickX,
        y: clickY,
        isTopHalf: isTopHalf,
      };

      // Set the editingDepositGroupValue to the current depositGroup
      this.editingDepositGroupValue = firstDepositGroup || '';

      // Show the modal
      this.showEditDepositGroupModal = true;

      // Focus on the input after the modal is shown
      this.$nextTick(() => {
        if (this.$refs.depositGroupInput) {
          this.$refs.depositGroupInput.focus();
          this.$refs.depositGroupInput.select();
        }
      });
    },

    getModalStyle() {
      // Calculate the position so the top-right corner of the modal is 20px left of click
      const modalWidth = 400; // Based on edit-deposit-modal max-width in CSS
      const rightEdgeOffset = 20;

      // Calculate desired left position (top-right corner 20px left of click)
      let leftPosition = this.modalPosition.x - modalWidth - rightEdgeOffset;

      // Ensure modal doesn't go off the left edge of the screen
      leftPosition = Math.max(10, leftPosition);

      // For bottom positioning, ensure modal doesn't go off right edge
      if (leftPosition + modalWidth > window.innerWidth - 10) {
        leftPosition = window.innerWidth - modalWidth - 10;
      }

      return {
        position: 'fixed',
        left: leftPosition + 'px',
        top: this.modalPosition.isTopHalf
          ? this.modalPosition.y + 'px'
          : 'auto',
        bottom: this.modalPosition.isTopHalf
          ? 'auto'
          : window.innerHeight - this.modalPosition.y + 'px',
        transformOrigin: this.modalPosition.isTopHalf
          ? 'top right'
          : 'bottom right',
        zIndex: 2001,
      };
    },

    saveDepositGroup() {
      // Update the depositGroup for all selected payments
      const newDepositGroup = this.editingDepositGroupValue.trim();
      const paymentIds = this.selectedPayments.map((payment) => payment.id);

      // Call the API to update the depositGroup
      axios
        .post('/api/update-deposit-group', {
          paymentIds: paymentIds,
          depositGroup: newDepositGroup,
        })
        .then(() => {
          // Update local data
          this.selectedPayments.forEach((payment) => {
            // Update in selectedPayments
            payment.depositGroup = newDepositGroup;

            // Update in paymentData
            const paymentIndex = this.paymentData.findIndex(
              (p) => p.id === payment.id,
            );
            if (paymentIndex !== -1) {
              this.paymentData[paymentIndex].depositGroup = newDepositGroup;
            }
          });

          this.footerMessage = `Updated deposit group to "${newDepositGroup}" for ${this.selectedPayments.length} payments`;
          this.showEditDepositGroupModal = false;
        })
        .catch((error) => {
          console.error('Error updating deposit group:', error);
          this.footerMessage =
            'Failed to update deposit group. Please try again.';
        });
    },

    cancelDepositGroupEdit() {
      this.showEditDepositGroupModal = false;
    },

    openReassignModal() {
      if (this.selectedPayments.length !== 1) {
        this.footerMessage = 'Please select exactly one payment to reassign.';
        return;
      }
      this.showReassignModal = true;
      this.reassignSearchQuery = '';
      this.reassignSuggestions = [];
      this.showReassignSuggestions = false;
      this.selectedReassignIndex = -1;
      this.selectedReassignTenant = null;
      this.$nextTick(() => {
        if (this.$refs.tenantSearchInput) {
          this.$refs.tenantSearchInput.focus();
        }
      });
    },

    closeReassignModal() {
      this.showReassignModal = false;
      this.reassignSearchQuery = '';
      this.reassignSuggestions = [];
      this.showReassignSuggestions = false;
      this.selectedReassignIndex = -1;
      this.selectedReassignTenant = null;
    },

    async handleReassignSearch() {
      if (this.reassignSearchTimeout) {
        clearTimeout(this.reassignSearchTimeout);
      }

      const query = this.reassignSearchQuery.trim();

      if (query.length < 2) {
        this.reassignSuggestions = [];
        this.showReassignSuggestions = false;
        return;
      }

      this.reassignSearchTimeout = setTimeout(async () => {
        try {
          const response = await axios.get('/api/tenants/search', {
            params: { query },
          });
          this.reassignSuggestions = response.data;
          this.showReassignSuggestions = true;
          this.selectedReassignIndex = -1;
        } catch (error) {
          console.error('Error searching tenants:', error);
          this.reassignSuggestions = [];
        }
      }, 300);
    },

    handleReassignBlur() {
      setTimeout(() => {
        this.showReassignSuggestions = false;
        this.selectedReassignIndex = -1;
      }, 200);
    },

    handleReassignKeydown(event) {
      if (
        !this.showReassignSuggestions ||
        this.reassignSuggestions.length === 0
      ) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedReassignIndex = Math.min(
            this.selectedReassignIndex + 1,
            this.reassignSuggestions.length - 1,
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.selectedReassignIndex = Math.max(
            this.selectedReassignIndex - 1,
            -1,
          );
          break;
        case 'Enter':
        case 'Tab':
          event.preventDefault();
          if (this.selectedReassignIndex >= 0) {
            this.selectReassignTenant(
              this.reassignSuggestions[this.selectedReassignIndex],
            );
          } else if (this.reassignSuggestions.length === 1) {
            this.selectReassignTenant(this.reassignSuggestions[0]);
          }
          break;
        case 'Escape':
          this.showReassignSuggestions = false;
          this.selectedReassignIndex = -1;
          break;
      }
    },

    selectReassignTenant(tenant) {
      this.selectedReassignTenant = tenant;
      this.reassignSearchQuery = tenant.fullName;
      this.showReassignSuggestions = false;
      this.selectedReassignIndex = -1;
    },

    async updatePaymentTenant() {
      if (!this.selectedReassignTenant) {
        this.footerMessage = 'Please select a tenant first.';
        return;
      }

      if (this.selectedPayments.length !== 1) {
        this.footerMessage = 'Please select exactly one payment.';
        return;
      }

      const payment = this.selectedPayments[0];

      try {
        // Get the lease ID for the selected tenant
        const leaseResponse = await axios.get('/api/tenant-current-lease', {
          params: { peopleId: this.selectedReassignTenant.id },
        });

        if (!leaseResponse.data || !leaseResponse.data.leaseId) {
          this.footerMessage = `No active lease found for ${this.selectedReassignTenant.fullName}`;
          return;
        }

        const leaseId = leaseResponse.data.leaseId;

        // Update the payment
        await axios.post('/api/reassign-payment', {
          paymentId: payment.id,
          peopleId: this.selectedReassignTenant.id,
          leaseId: leaseId,
        });

        this.footerMessage = `Payment reassigned to ${this.selectedReassignTenant.fullName}`;
        this.closeReassignModal();
        // Socket event 'leasePaymentUpdated' will update the local array automatically
      } catch (error) {
        console.error('Error reassigning payment:', error);
        this.footerMessage =
          'Error reassigning payment: ' +
          (error.response?.data?.error || error.message);
      }
    },
  },

  watch: {
    // Watch for route query changes to handle search param updates
    '$route.query.search': {
      handler(newSearch) {
        if (newSearch) {
          this.inputQuery = newSearch;
          this.searchQuery = newSearch;

          // Check if search looks like MMDDYY.N format (date with sequence)
          const dateMatch = newSearch.match(/^(\d{2})(\d{2})(\d{2})\.?\d*$/);
          if (dateMatch) {
            const [, mm, dd, yy] = dateMatch;
            const year =
              parseInt(yy) < 50 ? 2000 + parseInt(yy) : 1900 + parseInt(yy);
            const month = parseInt(mm) - 1; // JS months are 0-indexed
            const day = parseInt(dd);
            const parsedDate = new Date(year, month, day);

            // Use parsed date as start date if valid
            if (!isNaN(parsedDate.getTime())) {
              this.startDate = parsedDate.toISOString().split('T')[0];
            } else {
              const currentYear = new Date().getFullYear();
              this.startDate = new Date(currentYear, 0, 1)
                .toISOString()
                .split('T')[0];
            }
          } else {
            const currentYear = new Date().getFullYear();
            this.startDate = new Date(currentYear, 0, 1)
              .toISOString()
              .split('T')[0];
          }

          this.latestPayment = null; // Reset to force full refresh with new date range
          this.fetchPayments();
        }
      },
      immediate: false,
    },

    // auto-open the purpose dropdown when the td is clicked

    refreshPaymentPlot(newVal) {
      if (newVal) {
        setTimeout(() => {
          this.refreshPaymentPlot = false;
        }, 1000);
      }
    },
    footerMessage(newVal) {
      if (newVal !== '') {
        setTimeout(() => {
          this.footerMessage = '';
        }, 8000);
      }
    },

    showToolsPage(isOpen) {
      if (isOpen) {
        this.$nextTick(() => this.initToolsPageDrag());
      } else if (this.toolsPageDragCleanup) {
        this.toolsPageDragCleanup();
        this.toolsPageDragCleanup = null;
      }
    },

    showUncollectedLeasesPopup(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.initUncollectedPopupDrag();
          this.constrainUncollectedPopupHeight();
        });
      } else if (this.uncollectedPopupDragCleanup) {
        this.uncollectedPopupDragCleanup();
        this.uncollectedPopupDragCleanup = null;
      }
    },
  },
};
</script>

<style scoped>
.people-page {
  margin-left: 25px;
  padding: 0px;
  width: 1150px;
  height: 95%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.page-title {
  font-size: 14pt;
  font-weight: bold;
}
.search-bar {
  margin-top: 15px;
  font-weight: bold;
  font-size: 0.9em;
  margin-bottom: 5px;
}
.search-bar input {
  height: 20px;
  width: 250px;
  border-radius: 4px;
  border-color: #ccc;
}

.search-bar button {
  height: 26px;
  border-radius: 4px;
  padding: 4px;
  border: 1px solid #999;
  margin-left: 2px;
  width: 28px;
}

.search-bar .label {
  margin-bottom: 13px;
}

.table-container {
  position: relative;
  width: 100%;
  user-select: none;
}

.table-header {
  overflow: hidden;
  padding-right: 15px;
  /* background: #888888; */
  margin-bottom: -1px;
}
.table-header i {
  padding-left: 4px;
}
.table-body {
  max-height: 395px; /* Set a height for the scrollable area */
  overflow-y: scroll; /* Enables vertical scrolling */
  display: block; /* Makes the body a block element for separate scrolling */
}

.table-body table,
.table-header table {
  width: 1136px; /* Ensures the table takes the full width of its container */
  border-collapse: collapse;
  font-size: 9pt;
  border: 0;
}

th,
td {
  text-align: left;
  padding: 4px; /* Add padding for better spacing */
  border-bottom: 0.1em solid #e4e4e4;
  border-left: 1pt solid #ccc;
}

th {
  min-width: 50px; /* Sets a minimum width for headers */
}

thead {
  background-color: #888;
  z-index: 1;
  font-size: 1.1em;
  top: 0; /* Sticks the header to the top of the container */
}

tbody tr {
  table-layout: auto; /* Allows columns to adjust based on content and width settings */
  cursor: pointer;
}

.selected-row {
  background-color: #baa1bd !important; /* Highlight selected row */
  /* color: #eee; */
}

.odd-row {
  background-color: #ddd; /* Example alternating row color */
}

.even-row {
  background-color: #ffffff;
}
.is-expired {
  color: #5f53c1a1;
  font-size: 1.1em;
}

.search-input {
  border-radius: 4px;
}

label {
  margin-left: 10px;
}

.date-picker {
  width: 100px !important;
}

.count {
  font-weight: normal;
}

.sum {
  font-weight: normal;
}
.footer-message {
  transition: background-color 0.5s ease; /* Smooth transition for the background fade */
  position: fixed; /* Position it absolutely relative to the parent container */
  border-radius: 4px;
  bottom: 0; /* Align it to the bottom of the parent container */
  left: 0;
  width: 100vw; /* Make it span the full width of the parent container */
  height: 20px; /* Set the desired height */
  text-align: left; /* Optional: Center text horizontally */
  line-height: 20px; /* Optional: Center text vertically within the 20px height */
  font-size: 0.8em;
  padding: 3px;
  padding-left: 6px;
  z-index: 1000; /* Ensure the footer stays on top of other elements */
  width: 1220px;
}

.footer-message.highlighted {
  border: 1pt solid #ddd;
  background-color: #9b7ba8; /* Your desired background color */
  color: white;
}

.tools-page-wrapper {
  position: fixed;
  top: 115px;
  left: 225px;
  width: 800px;
  height: 77%;
  background-color: #cdcdcd;
  border: 1px solid #888;
  box-shadow: 8px 13px 8px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
  padding: 20px;
  border-radius: 6px;
  /* Above NavBar floaters: quick email / mobile notes (10000), work orders stacking */
  z-index: 30000;
}

.tools-page-drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;
  width: 100%;
  padding: 6px 10px;
  text-align: center;
  font-style: italic;
  cursor: move;
  user-select: none;
  border-bottom: 1px solid #aaa;
  flex-shrink: 0;
}

.tools-page-drag-handle i {
  opacity: 0.55;
}

.notification-dot {
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
  position: absolute;
  top: -9px;
  right: -17px;
  width: 19px;
  height: 14px;
  background-color: red;
  border-radius: 50%;
  /* display: inline-block; */
  font-size: 0.7em;
  padding-top: 4px;
  color: white;
  z-index: 1000;
}

.address {
  text-decoration: underline;
  text-decoration-color: #ccc;
}

.fullName {
  text-decoration: underline;
  text-decoration-color: #ccc;
}

.Email {
  text-decoration: underline;
  text-decoration-color: #ccc;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-outer-wrapper {
  overflow: hidden;
  width: 1136px;
}

.table-inner-wrapper {
  width: 1154px;
  height: 420px;
  overflow-y: scroll;
  overflow-x: hidden;
  display: block;
}

.position-wrapper {
  position: relative; /* positions the select relative to this div */
}

.custom-select {
  position: absolute;
  top: -10px;
  left: -6px;
  width: auto;
  background-color: white;
  z-index: 1;
  border-radius: 4px;
  border: 1pt solid #aaa;
  font-size: 0.95em;
  height: 20px;
}

.filter-active {
  background-color: #b59cb8;
  border: 1px solid #555 !important;
}

/* Modal overlay fills the screen with semi-transparent background */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  z-index: 2000;
  display: flex;
  justify-content: left;
  align-items: center;
}

/* Modal content with a max-size and scrollable body */
.modal-content {
  background: white;
  border-radius: 6px;
  width: 80%;
  max-width: 680px;
  max-height: 80%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
  background: #333;
}

/* Close button styles */
.modal-close-button {
  align-self: flex-end;
  margin: 10px;
  cursor: pointer;
}

.modal-title {
  color: #fff;
  position: relative;
  top: 1;
  left: 50px;
  font-size: 1.5em;
  max-width: 75%;
  padding: 10px;
}

/* Modal body */
.modal-body {
  padding: 20px;
  padding-top: 5px;
  overflow-y: auto;
  flex-grow: 1;
  border-top: 1px solid #ddd;
  font-size: 0.8em;
  font-family: system-ui;
  background: #efefef;
}

/* Optional styling for the modal open button */
.modal-open-button {
  margin: 5px;
  margin-left: 19px;
  padding: 7px;
  font-size: 0.9em;
  cursor: pointer;
  margin-right: 15px;
  border: 1px solid #bbb;
  border-radius: 4px;
}

.date-container {
  margin-top: 8px;
  font-size: 0.7em;
  font-style: italic;
  border: 1px solid #bab3b3;
  background: #cecece;
  border-radius: 4px;
  padding-left: 8px;
  margin-right: 12px;
}

.modal-body :deep(table) {
  border-collapse: collapse !important;
  margin-bottom: 18px;
}

.modal-body :deep(th),
.modal-body :deep(td) {
  border: 1px solid #bbb !important;
  padding: 8px;
}

/* Add these to your <style scoped> section */
.edit-deposit-modal {
  max-width: 400px;
}

.edit-deposit-body {
  display: flex;
  flex-direction: column;
  padding: 20px;
}

.edit-deposit-input {
  padding: 8px;
  margin-bottom: 15px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 1em;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

.save-button,
.cancel-button {
  padding: 6px 15px;
  border-radius: 4px;
  border: 1px solid #ccc;
  cursor: pointer;
}

.save-button {
  background-color: #4caf50;
  color: white;
  border-color: #4caf50;
}

.save-button:hover {
  background-color: #45a049;
}

.cancel-button {
  background-color: #f1f1f1;
}

.cancel-button:hover {
  background-color: #e7e7e7;
}

/* Positioned modal overlay (no background, just positioning) */
.modal-overlay-positioned {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2000;
}

/* Positioned modal content */
.positioned-modal {
  pointer-events: auto;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  border: 1px solid #888;
}

/* Reassign Payment Modal */
.reassign-modal {
  background: white;
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10001;
  min-width: 450px;
  max-width: 550px;
  margin-left: 170px;
  height: 500px;
  display: flex;
  flex-direction: column;
}

.reassign-modal .modal-header {
  background: #6e5875;
  color: white;
  padding: 8px 20px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.reassign-modal .modal-title {
  font-size: 16px;
  font-weight: bold;
  margin: 0;
}

.reassign-modal .modal-close-button {
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reassign-modal .modal-close-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.reassign-modal .modal-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.reassign-modal .modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #ddd;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.autocomplete-wrapper {
  position: relative;
  width: 100%;
}

.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-top: none;
  max-height: 300px;
  overflow-y: auto;
  z-index: 10002;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.autocomplete-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.autocomplete-item:hover,
.autocomplete-item.highlighted {
  background-color: #f0f0f0;
}

.suggestion-name {
  font-weight: bold;
  margin-bottom: 4px;
}

.suggestion-details {
  font-size: 0.9em;
  color: #666;
}

.suggestion-phone {
  margin-right: 10px;
}

.suggestion-address {
  font-style: italic;
}

.selected-tenant-info {
  margin-top: 15px;
  padding: 10px;
  background-color: #f0f8ff;
  border: 1px solid #b0d4ff;
  border-radius: 4px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.form-group input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.modal-button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.modal-button.cancel {
  background-color: #f0f0f0;
  color: #333;
}

.modal-button.cancel:hover {
  background-color: #e0e0e0;
}

.modal-button.save {
  background-color: #4a90e2;
  color: white;
}

.modal-button.save:hover {
  background-color: #357abd;
}

.modal-button.save:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.uncollected-popup-content {
  position: fixed;
  top: 90px;
  left: 180px;
  height: 85vh;
  max-height: 85vh;
  background: white;
  border-radius: 8px;
  width: 440px;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid #888;
  /* Below Quick Email / LeaseState (10050+), above page content */
  z-index: 10002;
  pointer-events: auto;
}

.uncollected-popup-header {
  background: #a080a8;
  color: white;
  padding: 10px 15px;
  font-size: 1.2em;
  flex-shrink: 0;
}

.uncollected-popup-drag-handle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: move;
  user-select: none;
}

.uncollected-popup-drag-handle i {
  opacity: 0.75;
  font-size: 0.85em;
}

.uncollected-popup-body {
  padding: 15px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.uncollected-popup-loading {
  padding: 20px;
  text-align: center;
  color: #666;
}

.uncollected-popup-search {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
  flex-shrink: 0;
}

.uncollected-popup-search-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9em;
}

.uncollected-popup-search--balance {
  gap: 6px;
}

.uncollected-popup-search--balance .uncollected-popup-search-input {
  flex: 1;
  min-width: 0;
  width: auto;
}

.uncollected-balance-sort-toggle {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  font-size: 0.85em;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}

.uncollected-balance-sort-toggle:hover {
  background: #f0f0f0;
}

.uncollected-popup-clear-search {
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
}

.uncollected-leases-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.uncollected-leases-list::-webkit-scrollbar {
  display: none;
}

.uncollected-lease-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 2px;
  border-bottom: 1px solid #eee;
  font-size: 0.9em;
}

.uncollected-lease-info {
  flex: 1;
  min-width: 0;
}

.uncollected-lease-address {
  font-weight: bold;
  word-break: break-word;
}

.uncollected-lease-tenant {
  color: #555;
  margin-top: 2px;
}

.uncollected-lease-details {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  flex-shrink: 0;
}

.payment-lstat.lease-state {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85em;
  user-select: none;
}

.uncollected-lease-lstat.lease-state {
  min-width: 40px;
  max-width: 40px;
  text-align: center;
  font-size: 0.85em;
  padding: 1px 2px;
  border-radius: 2px;
  user-select: none;
}

.uncollected-lease-amount {
  font-weight: bold;
  white-space: nowrap;
  min-width: 52px;
  text-align: right;
}

.uncollected-lease-balance {
  font-weight: bold;
  white-space: nowrap;
  min-width: 64px;
  text-align: right;
}

.uncollected-grand-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 2px 6px;
  margin-top: 4px;
  border-top: 2px solid #ccc;
  font-weight: bold;
  font-size: 0.95em;
}

.uncollected-grand-total-label {
  color: #333;
}

.uncollected-grand-total-amount {
  min-width: 52px;
  text-align: right;
}

.uncollected-popup-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 10px 15px;
  background: #f5f5f5;
  flex-shrink: 0;
}

.uncollected-no-results {
  padding: 20px;
  text-align: center;
  color: #666;
}

.payment-warning-icon {
  color: #e6a817;
  margin-right: 4px;
}

.uncollected-popup-footer-actions {
  display: flex;
  gap: 8px;
}

.uncollected-popup-footer button {
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 3px;
}

.uncollected-popup-footer button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.uncollected-notify-overlay {
  position: fixed;
  inset: 0;
  z-index: 10020;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
}

.uncollected-notify-popup {
  width: 520px;
  max-width: calc(100vw - 32px);
  max-height: 85vh;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #888;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.uncollected-notify-header {
  background: #a080a8;
  color: #fff;
  padding: 10px 15px;
  font-size: 1.15em;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.uncollected-notify-count {
  font-size: 0.85em;
  opacity: 0.9;
}

.uncollected-notify-body {
  padding: 12px 15px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.uncollected-notify-to-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
}

.uncollected-notify-to-label {
  font-size: 0.8em;
  color: #666;
  margin-bottom: 4px;
}

.uncollected-notify-to-row .uncollected-notify-to-label {
  margin-bottom: 0;
  flex-shrink: 0;
}

.uncollected-notify-to {
  font-weight: 600;
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.uncollected-notify-balance {
  margin-left: auto;
  font-weight: 700;
  white-space: nowrap;
}

.uncollected-notify-preview-wrap {
  position: relative;
  margin: 0 0 12px;
}

.uncollected-notify-preview {
  font-family: inherit;
  font-size: 0.9em;
  line-height: 1.45;
  background: #f7f7f7;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px 12px;
  margin: 0;
  max-height: 320px;
  overflow-y: auto;
}

.uncollected-notify-sending {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.78);
  color: #444;
  font-size: 0.95em;
  border-radius: 4px;
}

.uncollected-notify-sending i {
  font-size: 1.6em;
  color: #a080a8;
}

.uncollected-notify-preview :deep(p) {
  margin: 0 0 0.8em;
}

.uncollected-notify-preview :deep(p:last-child) {
  margin-bottom: 0;
}

.uncollected-notify-preview :deep(a) {
  color: #1a5fb4;
}

.uncollected-notify-preview :deep(strong),
.uncollected-notify-preview :deep(b) {
  font-weight: 700;
}

.uncollected-notify-errors {
  color: #b00020;
  font-size: 0.85em;
  margin-top: 8px;
}

.uncollected-notify-done {
  padding: 24px 8px;
  text-align: center;
}

.uncollected-notify-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 15px;
  background: #f5f5f5;
}

.uncollected-notify-footer button {
  padding: 5px 12px;
  cursor: pointer;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.uncollected-notify-footer button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

/* Type column: reserve space for optional ChatBot mobile icon */
.td-payment-type {
  white-space: nowrap;
}

/* Check viewer (LeaseSearch-style, fixed so it stacks above PaymentViewer) */
.check-viewer {
  position: fixed;
  top: 18%;
  left: 50%;
  transform: translateX(-50%);
  width: 790px;
  height: 460px;
  z-index: 12000;
  border: 1px solid rgb(145, 146, 146);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 5px 6px 8px 5px rgba(0, 0, 0, 0.35);
}

.check-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #333;
  color: #eee;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  flex-shrink: 0;
}

.check-viewer-title {
  font-weight: bold;
  text-align: center;
  flex-grow: 1;
  cursor: move;
  padding: 6px 8px;
  user-select: none;
}

.check-viewer-title .draggable-icon {
  margin-left: 8px;
  opacity: 0.85;
}

.check-viewer-close-button {
  background: transparent;
  color: #eee;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 6px 14px;
}

.check-viewer-close-button:hover {
  color: #ff6666;
}

.check-viewer-frame {
  width: 100%;
  flex: 1;
  border: none;
  background: #f5f5f5;
}

.check-viewer-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  font-size: 0.95rem;
}
</style>
